import { useEffect, useState, useCallback } from "react";
import EventSpaceAdminPanel from "@/components/admin/EventSpaceAdminPanel";
import TicketOfferModal from "@/components/admin/TicketOfferModal";
import EventMeetingsAdmin from "@/components/admin/EventMeetingsAdmin";
import EventBulkEmailModal from "@/components/admin/EventBulkEmailModal";
import { useParams, Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail, getAuthToken } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import GooglePlacesInput from "@/components/GooglePlacesInput";
import { ArrowLeft, Plus, Trash2, Mic, Award, Users, QrCode, CheckCircle, Download, ScanLine, Euro, Receipt, Calendar as CalendarIcon, Clock, Ticket, UserPlus, FileSpreadsheet, FileText, Crown, Edit3, X, Save, Tag, RefreshCw, Link2, Send, Bell, Search, Loader2 } from "lucide-react";
import VisualDownloader from "@/components/VisualDownloader";
import DiffHistory from "@/components/DiffHistory";
import PublicLinkButton from "@/components/PublicLinkButton";

const LEVELS = ["Gold", "Silver", "Bronze", "Media", "Institutional", "Technical"];
const LEVEL_COLOR = {
  Gold: "bg-amber-100 text-amber-800 border-amber-200",
  Silver: "bg-neutral-200 text-neutral-700 border-neutral-300",
  Bronze: "bg-orange-100 text-orange-800 border-orange-200",
  Media: "bg-blue-50 text-blue-700 border-blue-200",
  Institutional: "bg-purple-50 text-purple-700 border-purple-200",
  Technical: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function AdminEventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [offer, setOffer] = useState(null); // { registration } | {} for new
  const [speakers, setSpeakers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [tab, setTab] = useState("speakers");
  const [sp, setSp] = useState({ name: "", role: "", company: "", bio: "", photo: "", linkedin: "" });
  const [sn, setSn] = useState({ name: "", level: "Gold", logo: "", description: "", url: "" });
  const [scanCode, setScanCode] = useState("");
  const [pricing, setPricing] = useState({ is_paid: false, price_member: 0, price_visitor: 0, price_exhibitor: 0, price_media: 0, currency: "eur" });
  const [transactions, setTransactions] = useState([]);
  const [revenue, setRevenue] = useState({ totals: [], paid_count: 0 });
  const [agenda, setAgenda] = useState([]);
  const [exhibitorsBySlot, setExhibitorsBySlot] = useState({});
  const [newSlot, setNewSlot] = useState({ title: "", animation_type: "conference", start_time: "", end_time: "", location: "", capacity: 0, description: "", sm_roles: [], sm_slot_duration: 10 });
  const [tickets, setTickets] = useState([]);
  // Sprint 92aj — Promo codes managed inside the "Tarifs" tab.
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromo, setNewPromo] = useState({ code: "", type: "percent", value: 10, ticket_ids: [], max_uses: "", expires_at: "", active: true });
  const [addons, setAddons] = useState([]);
  const [newAddon, setNewAddon] = useState({ name: "", price: 0, description: "" });
  const [newTicket, setNewTicket] = useState({ name: "", price: 0, description: "", role: "visitor", external_url: "" });
  const [addPart, setAddPart] = useState({ open: false, email: "", first_name: "", last_name: "", company: "", job_title: "", phone: "", participation_type: "visitor", ticket_id: "", notify: true, member_company_id: "", companyQuery: "", companyResults: [], contactQuery: "", contactResults: [] });
  const [exportCategory, setExportCategory] = useState("");
  const [editingEvent, setEditingEvent] = useState(null);
  const openEditEvent = () => {
    setEditingEvent({
      title: event.title || "",
      slug: event.slug || "",
      description: event.description || "",
      date: (event.date || "").slice(0, 16),
      location: event.location || "",
      city: event.city || "",
      country: event.country || "",
      format: event.format || "physical",
      audience: event.audience || "public",
      type: event.type || "",
      image: event.image || "",
      address: event.address || "",
      google_place_id: event.google_place_id || "",
      status: event.status || "published",
      virtual_link: event.virtual_link || "",
      lang: event.lang || "fr",
    });
  };
  const saveEvent = async () => {
    if (!editingEvent) return;
    try {
      const body = { ...editingEvent };
      if (body.date) body.date = new Date(body.date).toISOString();
      await api.patch(`/admin/events/${event.id}`, body);
      toast.success("Événement modifié");
      setEditingEvent(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Erreur");
    }
  };
  const ANIMATIONS = {
    round_table: "Table ronde", conference: "Conférence", speed_meeting: "RDV 1:1 individuel",
    workshop: "Atelier", demo: "Démonstration", cocktail: "Cocktail", informal: "Échanges informels",
  };
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolve = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

  const load = useCallback(async () => {
    const r = await api.get("/admin/events", { params: { limit: 500 } });
    const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
    const ev = arr.find((e) => e.id === id);
    setEvent(ev);
    if (ev) {
      api.get(`/events/${ev.id}/speakers`).then((r) => setSpeakers(r.data));
      api.get(`/events/${ev.id}/sponsors`).then((r) => setSponsors(r.data));
      api.get(`/events/${ev.id}/attendees`).then((r) => setAttendees(r.data));
      api.get(`/admin/events/${ev.id}/pricing`).then((r) => setPricing(r.data)).catch(() => {});
      api.get(`/admin/events/${ev.id}/transactions`).then((r) => setTransactions(r.data)).catch(() => {});
      api.get(`/admin/events/${ev.id}/revenue`).then((r) => setRevenue(r.data)).catch(() => {});
      setTickets(ev.tickets || []);
      setPromoCodes(ev.promo_codes || []);
      setAddons(ev.add_on_services || []);
      api.get(`/events/${ev.id}/agenda`).then((r) => {
        const list = r.data || [];
        setAgenda(list);
        // Load exhibitors for each speed_meeting slot
        list.filter((s) => s.animation_type === "speed_meeting").forEach((s) => {
          api.get(`/admin/events/${ev.id}/agenda/${s.id}/exhibitors`)
            .then((re) => setExhibitorsBySlot((prev) => ({ ...prev, [s.id]: re.data })))
            .catch(() => {});
        });
      }).catch(() => {});
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addSpeaker = async (e) => {
    e.preventDefault();
    try { await api.post(`/events/${id}/speakers`, sp); toast.success("Intervenant ajouté"); setSp({ name: "", role: "", company: "", bio: "", photo: "", linkedin: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delSpeaker = async (sid) => { await api.delete(`/speakers/${sid}`); load(); };

  const addSponsor = async (e) => {
    e.preventDefault();
    try { await api.post(`/events/${id}/sponsors`, sn); toast.success("Sponsor ajouté"); setSn({ name: "", level: "Gold", logo: "", description: "", url: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delSponsor = async (sid) => { await api.delete(`/sponsors/${sid}`); load(); };

  const checkin = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/events/${id}/checkin`, { badge_code: scanCode.trim() });
      if (data.already_checked_in) toast.warning(`${data.user_name} déjà check-in`);
      else toast.success(`✓ ${data.user_name} (${data.company || ""}) check-in`);
      setScanCode("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const savePricing = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/events/${id}/pricing`, pricing);
      toast.success("Tarification enregistrée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fmtMoney = (amt, cur) => `${(amt || 0).toFixed(2)} ${(cur || "eur").toUpperCase()}`;

  const addSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.title || !newSlot.start_time || !newSlot.end_time) { toast.error("Titre, début et fin requis"); return; }
    try {
      await api.post(`/admin/events/${id}/agenda`, newSlot);
      toast.success("Créneau ajouté");
      setNewSlot({ title: "", animation_type: "conference", start_time: "", end_time: "", location: "", capacity: 0, description: "", sm_roles: [], sm_slot_duration: 10 });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const delSlot = async (slotId) => {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    try { await api.delete(`/admin/events/${id}/agenda/${slotId}`); load(); } catch { toast.error("Erreur"); }
  };

  // Drag & drop reordering of agenda slots
  const [dragSlotId, setDragSlotId] = useState(null);
  const onDragStart = (e, slotId) => {
    setDragSlotId(slotId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", slotId); } catch { /* legacy browsers */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = async (e, targetSlotId) => {
    e.preventDefault();
    const sourceId = dragSlotId || e.dataTransfer.getData("text/plain");
    setDragSlotId(null);
    if (!sourceId || sourceId === targetSlotId) return;
    const order = agenda.map((s) => s.id);
    const srcIdx = order.indexOf(sourceId);
    const tgtIdx = order.indexOf(targetSlotId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    order.splice(srcIdx, 1);
    order.splice(tgtIdx, 0, sourceId);
    // Optimistic local reorder
    const byId = Object.fromEntries(agenda.map((s) => [s.id, s]));
    setAgenda(order.map((sid) => byId[sid]));
    try {
      await api.post(`/admin/events/${id}/agenda/reorder`, { order });
      toast.success("Programme réordonné");
    } catch (err) {
      toast.error("Erreur de réordonnancement");
      load();  // reload from server on failure
    }
  };

  // Inline edit of a slot
  const [editingSlot, setEditingSlot] = useState(null);
  const startEdit = (s) => setEditingSlot({ ...s, start_time: (s.start_time || "").slice(0, 16), end_time: (s.end_time || "").slice(0, 16) });
  const saveEdit = async () => {
    if (!editingSlot || !editingSlot.title) return;
    try {
      const payload = { ...editingSlot };
      await api.patch(`/admin/events/${id}/agenda/${editingSlot.id}`, payload);
      toast.success("Créneau mis à jour");
      setEditingSlot(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const addRole = () => {
    const role = window.prompt("Nom du rôle (ex: Acheteur, Vendeur, Investisseur…)")?.trim();
    if (role) setNewSlot({ ...newSlot, sm_roles: [...newSlot.sm_roles, role] });
  };

  const moderateExhibitor = async (slotId, exhId, status) => {
    try {
      await api.patch(`/admin/events/${id}/exhibitors/${exhId}`, { status });
      toast.success(`Exposant ${status === "approved" ? "validé" : "refusé"}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteExhibitor = async (exhId) => {
    if (!window.confirm("Supprimer cet exposant et tous ses RDV ?")) return;
    try {
      await api.delete(`/admin/events/${id}/exhibitors/${exhId}`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const markExhibitorPaid = async (exhId, paid) => {
    const note = paid ? (window.prompt("Note (référence virement, n° de chèque…) — optionnel :") || "") : "";
    try {
      await api.post(`/admin/events/${id}/exhibitors/${exhId}/mark-paid`, { paid, method: "transfer", note });
      toast.success(paid ? "Exposant marqué payé" : "Marquage paiement retiré");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  // Sprint 73 — Notify contacts about this event
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [selContacts, setSelContacts] = useState(new Set());
  const [extraEmails, setExtraEmails] = useState("");
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  // Sprint 92x — Segment/tag/pole targeting for event notifications
  const [notifyTags, setNotifyTags] = useState(new Set());
  const [notifyPoles, setNotifyPoles] = useState(new Set());
  const [notifyAllNewsletter, setNotifyAllNewsletter] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);

  const openNotify = async () => {
    setNotifyOpen(true);
    if (contacts.length === 0) {
      setContactsLoading(true);
      try { const r = await api.get("/admin/contacts", { params: { per_page: 200 } }); setContacts(r.data?.items || r.data || []); }
      catch { toast.error("Erreur chargement contacts"); }
      finally { setContactsLoading(false); }
    }
    if (availableTags.length === 0) {
      try { const r = await api.get("/admin/contacts/tags"); setAvailableTags(r.data || []); }
      catch { /* ignore */ }
    }
  };
  const toggleNotifyContact = (cid) => { const n = new Set(selContacts); n.has(cid) ? n.delete(cid) : n.add(cid); setSelContacts(n); };
  const toggleNotifyTag = (t) => { const n = new Set(notifyTags); n.has(t) ? n.delete(t) : n.add(t); setNotifyTags(n); };
  const toggleNotifyPole = (p) => { const n = new Set(notifyPoles); n.has(p) ? n.delete(p) : n.add(p); setNotifyPoles(n); };
  const filteredNotifyContacts = contactQuery
    ? contacts.filter((c) => `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.company_name || ""}`.toLowerCase().includes(contactQuery.toLowerCase()))
    : contacts;

  const sendNotify = async (e) => {
    e?.preventDefault();
    const emails = extraEmails.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    const hasSegment = notifyTags.size > 0 || notifyPoles.size > 0 || notifyAllNewsletter;
    if (selContacts.size === 0 && emails.length === 0 && !hasSegment) {
      return toast.error("Sélectionnez au moins un contact, un segment (tag/pôle/newsletter) ou ajoutez un email");
    }
    setNotifySending(true);
    try {
      const r = await api.post(`/admin/events/${id}/notify-contacts`, {
        contact_ids: Array.from(selContacts),
        emails,
        tags: Array.from(notifyTags),
        poles: Array.from(notifyPoles),
        all_newsletter: notifyAllNewsletter,
        message: notifyMsg,
      });
      toast.success(`Notification envoyée à ${r.data.sent} destinataire${r.data.sent > 1 ? "s" : ""}`);
      setNotifyOpen(false); setSelContacts(new Set()); setNotifyTags(new Set()); setNotifyPoles(new Set()); setNotifyAllNewsletter(false); setExtraEmails(""); setNotifyMsg("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setNotifySending(false); }
  };


  const markRegistrationPaid = async (regId, paid) => {
    const note = paid ? (window.prompt("Note (référence virement, n° de chèque…) — optionnel :") || "") : "";
    try {
      await api.post(`/admin/event-registrations/${regId}/mark-paid`, { paid, method: "transfer", note });
      toast.success(paid ? "Participant marqué payé" : "Marquage paiement retiré");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const deleteAttendee = async (a) => {
    if (!window.confirm(`Supprimer la participation de ${a.user_name || a.user_email} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/event-registrations/${a.id}`);
      toast.success("Participant supprimé");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const resendStripeLink = async (a) => {
    if (!window.confirm(`Renvoyer à ${a.user_email} le lien Stripe pour finaliser le paiement de ${a.ticket_name || a.participation_type} (${a.price} ${(a.currency||'eur').toUpperCase()}) ?`)) return;
    try {
      const r = await api.post(`/admin/event-registrations/${a.id}/resend-stripe-link`);
      toast.success(`Lien Stripe renvoyé à ${r.data.to}${r.data.lang ? ` (${r.data.lang.toUpperCase()})` : ""}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  // Regenerate a fresh Stripe link (use after ticket type/price change, or expired session).
  const regenerateStripeLink = async (a, sendEmail = false) => {
    const action = sendEmail ? "régénérer + envoyer par email" : "régénérer le lien (copie clipboard)";
    if (!window.confirm(`${action} pour ${a.user_email} — ticket ${a.ticket_name || "—"} (${a.price} ${(a.currency || "eur").toUpperCase()}) ?`)) return;
    try {
      const r = await api.post(`/admin/event-registrations/${a.id}/regenerate-stripe-link`, {
        origin_url: window.location.origin,
        also_send_email: sendEmail,
      });
      if (sendEmail) {
        toast.success(`✉️ Nouveau lien envoyé à ${r.data.recipient_email}`);
      } else {
        try { await navigator.clipboard.writeText(r.data.checkout_url); } catch (_) { /* clipboard blocked — non-blocking */ }
        toast.success("🔗 Nouveau lien Stripe copié dans le presse-papier");
      }
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  // Edit participant (change role, ticket, name, etc.)
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [savingAttendee, setSavingAttendee] = useState(false);
  const saveAttendee = async () => {
    if (!editingAttendee) return;
    if (!editingAttendee.id) {
      toast.error("Impossible de modifier : participant sans identifiant. Rechargez la page.");
      return;
    }
    setSavingAttendee(true);
    try {
      const payload = {
        first_name: editingAttendee.first_name || "",
        last_name: editingAttendee.last_name || "",
        company: editingAttendee.company || "",
        job_title: editingAttendee.job_title || "",
        linkedin: editingAttendee.linkedin || "",
        participation_type: editingAttendee.participation_type || "visitor",
        ticket_id: editingAttendee.ticket_id || null,
      };
      await api.patch(`/admin/event-registrations/${editingAttendee.id}`, payload);
      toast.success("Participant mis à jour");
      setEditingAttendee(null);
      load();
    } catch (err) {
      console.error("saveAttendee error:", err.response?.status, err.response?.data, err.message);
      const detail = err.response?.data?.detail;
      const msg = formatApiErrorDetail(detail) || err.message || "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setSavingAttendee(false);
    }
  };

  // Bulk select + send catalog
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const toggleSelected = (id) => setSelectedAttendees((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  // Sprint 75 — Cancel event + Bulk-email participants (cancellation/reminder/custom)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("custom"); // 'cancellation' | 'reminder' | 'custom'
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const openEmailModal = (template = "custom") => {
    if (selectedAttendees.length === 0) { toast.error("Sélectionnez au moins un participant"); return; }
    setEmailTemplate(template);
    if (template === "cancellation") {
      setEmailSubject(`❗ Événement annulé — ${event?.title || ""}`);
      setEmailBody(`<p>Nous sommes au regret de vous informer que l'événement <strong>${event?.title || ""}</strong> est <strong>annulé</strong>.</p>\n<p>Si vous aviez réservé un billet payant, nous vous rembourserons sous 10 jours ouvrés.</p>\n<p>Nous restons à votre disposition pour toute question.</p>\n<p>L'équipe La WineTech</p>`);
    } else if (template === "reminder") {
      setEmailSubject(`Rappel — ${event?.title || ""}`);
      setEmailBody(`<p>Petit rappel concernant <strong>${event?.title || ""}</strong>.</p>\n<p>À très vite !</p>`);
    } else {
      setEmailSubject("");
      setEmailBody("");
    }
    setEmailOpen(true);
  };
  const sendBulkEmail = async (e) => {
    e?.preventDefault();
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error("Sujet et message requis"); return; }
    setEmailSending(true);
    try {
      const r = await api.post(`/admin/events/${id}/registrations/bulk-email`, {
        ids: selectedAttendees,
        subject: emailSubject,
        body: /<[a-z][\s\S]*>/i.test(emailBody) ? emailBody : emailBody.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join(""),
        template: emailTemplate,
      });
      toast.success(`Email envoyé à ${r.data.sent} participant${r.data.sent > 1 ? "s" : ""}${r.data.failed?.length ? ` (${r.data.failed.length} échec${r.data.failed.length > 1 ? "s" : ""})` : ""}`);
      setEmailOpen(false); setSelectedAttendees([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally { setEmailSending(false); }
  };

  const toggleEventCancelled = async () => {
    const willCancel = !event?.is_cancelled;
    const msg = willCancel
      ? `Annuler définitivement « ${event?.title || ""} » ?\n\nUn filigrane "ANNULÉ" sera affiché sur la page publique. Vous pourrez ensuite notifier les participants par email.`
      : `Réactiver « ${event?.title || ""} » ? Le filigrane d'annulation sera retiré.`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/admin/events/${id}`, { is_cancelled: willCancel });
      toast.success(willCancel ? "Événement annulé" : "Événement réactivé");
      load();
      if (willCancel) {
        // Auto-select all attendees so admin can immediately notify them
        const allIds = (attendees || []).map((r) => r.id);
        setSelectedAttendees(allIds);
        setTimeout(() => openEmailModal("cancellation"), 400);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };
  const sendCatalogToSelected = async () => {
    if (selectedAttendees.length === 0) { toast.error("Sélectionnez au moins un participant"); return; }
    if (!window.confirm(`Envoyer le catalogue PDF à ${selectedAttendees.length} participant${selectedAttendees.length > 1 ? "s" : ""} ?`)) return;
    try {
      const r = await api.post(`/admin/events/${id}/send-catalog`, { registration_ids: selectedAttendees });
      toast.success(`Catalogue envoyé à ${r.data.sent} destinataire${r.data.sent > 1 ? "s" : ""}`);
      setSelectedAttendees([]);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const bulkDeleteAttendees = async () => {
    if (selectedAttendees.length === 0) { toast.error("Sélectionnez au moins un participant"); return; }
    if (!window.confirm(`Supprimer définitivement ${selectedAttendees.length} participant${selectedAttendees.length > 1 ? "s" : ""} ? Cette action est irréversible.`)) return;
    try {
      const r = await api.post(`/admin/event-registrations/bulk-delete`, { ids: selectedAttendees });
      toast.success(`${r.data.deleted} participant${r.data.deleted > 1 ? "s" : ""} supprimé${r.data.deleted > 1 ? "s" : ""}`);
      setSelectedAttendees([]);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  // Smart Reminders — manual trigger from admin UI
  const sendEventReminders = async (daysLabel) => {
    const label = daysLabel === "j7" ? "J-7 (7 jours avant)" : "J-1 (la veille)";
    if (!window.confirm(`Envoyer le rappel ${label} à tous les participants ?\n\n` +
        "• Inscrits payés : rappel + programme + catalogue PDF\n" +
        "• Non payés : rappel + NOUVEAU lien Stripe (auto-régénéré) + catalogue\n\n" +
        "Idempotent — ne ré-envoie pas si déjà envoyé (sauf si vous confirmez le forçage).")) return;
    try {
      const r = await api.post(`/admin/events/${id}/send-reminders`, { days_label: daysLabel, force: true });
      if (r.data.skipped) {
        toast.info(`Déjà envoyé — utilisez le bouton de forçage si vous voulez ré-envoyer`);
      } else {
        toast.success(`✅ ${r.data.sent} email${r.data.sent > 1 ? "s" : ""} envoyé${r.data.sent > 1 ? "s" : ""}${r.data.regenerated_stripe_links > 0 ? ` · ${r.data.regenerated_stripe_links} lien Stripe régénéré${r.data.regenerated_stripe_links > 1 ? "s" : ""}` : ""}`);
      }
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const deleteTransaction = async (t) => {
    if (!window.confirm(`Supprimer la transaction de ${t.user_email || t.attendee_name || t.id} ? Le paiement Stripe n'est PAS remboursé automatiquement.`)) return;
    try {
      await api.delete(`/admin/transactions/${t.id}`);
      toast.success("Transaction supprimée");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const downloadAttendees = async (format) => {
    const tid = toast.loading(`Préparation du ${format.toUpperCase()}…`);
    try {
      // Use the non-/admin route to bypass aggressive ad-blockers that match '/admin/' in URLs
      const url = `/events/${id}/export/attendees.${format}${format === "pdf" && exportCategory ? `?category=${encodeURIComponent(exportCategory)}` : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      console.log("[export]", format, "status:", res.status, "size:", res.data?.size, "type:", res.data?.type);
      if (!res.data || res.data.size === 0) {
        toast.error("Fichier vide reçu du serveur", { id: tid });
        return;
      }
      const blob = new Blob([res.data], { type: format === "csv" ? "text/csv;charset=utf-8" : "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = blobUrl;
      a.download = `${format === "csv" ? "participants" : "catalogue"}-${event.slug}${exportCategory ? "-" + exportCategory : ""}.${format}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
      toast.success(`${format.toUpperCase()} téléchargé`, { id: tid });
    } catch (err) {
      console.error("[export] error:", err, err?.response);
      toast.error(`Erreur export ${format.toUpperCase()} : ${err?.response?.status || err?.message || "inconnu"}`, { id: tid });
    }
  };

  const saveTickets = async (e, nextTickets) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const payload = Array.isArray(nextTickets) ? nextTickets : tickets;
    try {
      const { data } = await api.patch(`/admin/events/${id}/tickets`, { tickets: payload });
      setTickets(data.tickets || []);
      toast.success("Tickets enregistrés");
      return true;
    } catch { toast.error("Erreur"); return false; }
  };

  const saveAddons = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/admin/events/${id}/add-ons`, { add_on_services: addons });
      setAddons(data.add_on_services || []);
      toast.success("Services additionnels enregistrés");
    } catch { toast.error("Erreur"); }
  };

  const addParticipant = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...addPart, event_id: id };
      if (!payload.ticket_id) delete payload.ticket_id;
      if (!payload.member_company_id) delete payload.member_company_id;
      delete payload.open;
      delete payload.companyQuery;
      delete payload.companyResults;
      delete payload.contactQuery;
      delete payload.contactResults;
      const { data } = await api.post(`/admin/events/${id}/invite-participant`, payload);
      const msgs = [];
      if (data.account_created) msgs.push("compte créé");
      if (data.activation_url) msgs.push("email d'activation envoyé");
      if (data.checkout_url) msgs.push("lien de paiement Stripe envoyé");
      if (data.company_licence_included) msgs.push("licence entreprise activée (gratuit)");
      if (data.price === 0 && !data.company_licence_included) msgs.push("inscription gratuite confirmée");
      toast.success(`Participant ajouté · ${msgs.join(" · ") || "ok"}`);
      setAddPart({ open: false, email: "", first_name: "", last_name: "", company: "", job_title: "", phone: "", participation_type: "visitor", ticket_id: "", notify: true, member_company_id: "", companyQuery: "", companyResults: [], contactQuery: "", contactResults: [] });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const searchCompanyForInvite = async (q) => {
    setAddPart((p) => ({ ...p, companyQuery: q }));
    if (q.length < 2) { setAddPart((p) => ({ ...p, companyResults: [] })); return; }
    try {
      const r = await api.get("/admin/companies", { params: { q, limit: 6 } });
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setAddPart((p) => ({ ...p, companyResults: items }));
    } catch { setAddPart((p) => ({ ...p, companyResults: [] })); }
  };

  // Existing-contact autocomplete (pre-fills email + names + company)
  const searchContactForInvite = async (q) => {
    setAddPart((p) => ({ ...p, contactQuery: q }));
    if (q.length < 2) { setAddPart((p) => ({ ...p, contactResults: [] })); return; }
    try {
      const r = await api.get("/admin/contacts", { params: { q, limit: 8 } });
      const items = (r.data?.items || r.data || []).slice(0, 8);
      setAddPart((p) => ({ ...p, contactResults: items }));
    } catch { setAddPart((p) => ({ ...p, contactResults: [] })); }
  };

  const pickContactForInvite = (c) => {
    setAddPart((p) => ({
      ...p,
      email: c.email || p.email,
      first_name: c.first_name || p.first_name,
      last_name: c.last_name || p.last_name,
      company: c.company_name || c.linked_company_name || p.company,
      job_title: c.job_title || p.job_title,
      phone: c.phone || p.phone,
      member_company_id: c.linked_company_id || p.member_company_id,
      contactQuery: "",
      contactResults: [],
    }));
  };

  const pickCompanyForInvite = (m) => {
    setAddPart((p) => ({ ...p, member_company_id: m.id, company: m.name, companyQuery: "", companyResults: [] }));
  };

  if (!event) return <PortalLayout sidebar="admin"><div className="py-20 text-center">Chargement...</div></PortalLayout>;

  const presentCount = attendees.filter((a) => a.checked_in).length;
  const resyncRoles = async () => {
    if (!window.confirm("Aligner le statut (visiteur / exposant / média) de chaque participant sur le rôle de son billet ? Les statuts forcés manuellement sont conservés.")) return;
    try {
      const { data } = await api.post(`/admin/events/${id}/registrations/resync-roles`);
      toast.success(data.updated ? `${data.updated} statut(s) corrigé(s) : ${data.changes.slice(0, 5).map((c) => c.name).join(", ")}${data.updated > 5 ? "…" : ""}` : "Tous les statuts sont déjà à jour");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };
  const tabs = [
    { id: "speakers", label: "Intervenants", icon: Mic, count: speakers.length },
    { id: "sponsors", label: "Sponsors", icon: Award, count: sponsors.length },
    { id: "attendees", label: "Participants", icon: Users, count: attendees.length },
    { id: "agenda", label: "Programme", icon: CalendarIcon, count: agenda.length },
    { id: "checkin", label: "Check-in", icon: ScanLine, count: presentCount },
    { id: "tickets", label: "Tarifs", icon: Ticket, count: tickets.length + (promoCodes.length ? 0 : 0) },
    // Sprint 92aj — "Tarification" tab removed (doublon de Tarifs/Tickets). Legacy
    // pricing data is still shown read-only in the Tarifs summary card.
    { id: "transactions", label: "Transactions", icon: Receipt, count: transactions.length },
    { id: "budget", label: "Budget", icon: Euro, count: 0 },
    { id: "space", label: "Espace participant", icon: Users, count: 0 },
    { id: "meetings", label: "RDV speed meeting", icon: Clock, count: null },
  ];

  return (
    <PortalLayout sidebar="admin">
      <Link to="/admin/events" className="text-sm font-semibold text-wine-600 hover:text-wine-700" data-testid="back-to-events"><ArrowLeft className="inline h-4 w-4" /> Retour aux événements</Link>
      <h1 className="mt-4 text-3xl font-bold text-navy md:text-4xl">{event.title}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={openEditEvent} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="edit-event-btn">
          <Edit3 className="h-3 w-3" /> Modifier l&apos;événement
        </button>
        <PublicLinkButton slug={event.slug} type="event" />
        <VisualDownloader kind="event" slug={event.slug} />
        <Link to={`/admin/events/${event.id}/visuals`} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100" data-testid="event-visuals-editor">
          ✨ Mini-Canva éditeur
        </Link>
        <button onClick={async () => {
          if (!window.confirm("Envoyer le badge PDF par email à tous les participants confirmés ?")) return;
          try {
            const r = await api.post(`/admin/events/${event.id}/send-badges`);
            toast.success(`${r.data.sent} badge(s) envoyé(s), ${r.data.skipped} déjà reçus.`);
          } catch (err) { toast.error("Erreur"); }
        }} className="inline-flex items-center gap-1.5 rounded-full border border-navy/30 bg-navy/5 px-3 py-1.5 text-xs font-bold text-navy hover:bg-navy/10" data-testid="send-all-badges">📨 Envoyer badges par email</button>
        <button onClick={openNotify} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-600" data-testid="event-notify-contacts-btn">
          <Bell className="h-3 w-3" /> Notifier des contacts
        </button>
      </div>
      <p className="mt-1 text-neutral-600">{event.location} · {new Date(event.date).toLocaleDateString("fr", { dateStyle: "full" })}</p>

      <div className="mt-6 inline-flex flex-wrap gap-1 rounded-full bg-wine-50/40 p-1">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${tab === t.id ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid={`tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label} {t.count !== null && <span className="ml-1 rounded-full bg-wine-100 px-1.5 py-0.5 text-[10px] text-wine-700">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "speakers" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_2fr]">
          <form onSubmit={addSpeaker} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-5">
            <h3 className="font-bold text-navy">Ajouter un intervenant</h3>
            <input required value={sp.name} onChange={(e) => setSp({ ...sp, name: e.target.value })} placeholder="Nom complet" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="speaker-name" />
            <input required value={sp.role} onChange={(e) => setSp({ ...sp, role: e.target.value })} placeholder="Fonction" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="speaker-role" />
            <input value={sp.company} onChange={(e) => setSp({ ...sp, company: e.target.value })} placeholder="Entreprise" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="speaker-company" />
            <input value={sp.linkedin} onChange={(e) => setSp({ ...sp, linkedin: e.target.value })} placeholder="LinkedIn URL" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="speaker-linkedin" />
            <textarea rows={3} value={sp.bio} onChange={(e) => setSp({ ...sp, bio: e.target.value })} placeholder="Bio" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="speaker-bio" />
            <ImageUpload value={sp.photo} onChange={(url) => setSp({ ...sp, photo: url })} label="Photo" testId="speaker-photo" />
            <button type="submit" className="w-full rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="speaker-submit"><Plus className="inline h-3.5 w-3.5" /> Ajouter</button>
          </form>
          <div className="grid gap-3 sm:grid-cols-2">
            {speakers.map((s) => (
              <div key={s.id} className="flex gap-3 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`speaker-${s.id}`}>
                {s.photo && <img src={resolve(s.photo)} alt={s.name} className="h-16 w-16 shrink-0 rounded-full object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy">{s.name}</div>
                  <div className="text-xs text-neutral-500">{s.role} · {s.company}</div>
                  <p className="mt-1 line-clamp-2 text-xs text-neutral-600">{s.bio}</p>
                </div>
                <button onClick={() => delSpeaker(s.id)} className="text-red-500 hover:text-red-700" data-testid={`speaker-del-${s.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {speakers.length === 0 && <div className="sm:col-span-2 rounded-xl border border-dashed border-neutral-200 p-6 text-center text-sm text-neutral-500">Aucun intervenant.</div>}
          </div>
        </div>
      )}

      {tab === "sponsors" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_2fr]">
          <form onSubmit={addSponsor} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-5">
            <h3 className="font-bold text-navy">Ajouter un sponsor</h3>
            <input required value={sn.name} onChange={(e) => setSn({ ...sn, name: e.target.value })} placeholder="Nom" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sponsor-name" />
            <select value={sn.level} onChange={(e) => setSn({ ...sn, level: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sponsor-level">{LEVELS.map((l) => <option key={l}>{l}</option>)}</select>
            <input value={sn.url} onChange={(e) => setSn({ ...sn, url: e.target.value })} placeholder="URL site web" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sponsor-url" />
            <textarea rows={2} value={sn.description} onChange={(e) => setSn({ ...sn, description: e.target.value })} placeholder="Description" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sponsor-desc" />
            <ImageUpload value={sn.logo} onChange={(url) => setSn({ ...sn, logo: url })} label="Logo" testId="sponsor-logo" />
            <button type="submit" className="w-full rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="sponsor-submit"><Plus className="inline h-3.5 w-3.5" /> Ajouter</button>
          </form>
          <div className="space-y-4">
            {LEVELS.map((lvl) => {
              const grp = sponsors.filter((s) => s.level === lvl);
              if (grp.length === 0) return null;
              return (
                <div key={lvl}>
                  <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{lvl}</div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {grp.map((s) => (
                      <div key={s.id} className={`flex items-center gap-3 rounded-xl border-2 p-3 ${LEVEL_COLOR[s.level]}`} data-testid={`sponsor-${s.id}`}>
                        {s.logo && <img src={resolve(s.logo)} alt={s.name} className="h-10 w-10 rounded-lg object-contain bg-white p-0.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm">{s.name}</div>
                          {s.description && <div className="text-[11px] opacity-80 line-clamp-1">{s.description}</div>}
                        </div>
                        <button onClick={() => delSponsor(s.id)} className="hover:text-red-700" data-testid={`sponsor-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {sponsors.length === 0 && <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-center text-sm text-neutral-500">Aucun sponsor.</div>}
          </div>
        </div>
      )}

      {tab === "attendees" && (
        <div className="mt-6 space-y-4">
          {/* Pricing summary — at-a-glance reminder of the tickets configured for this event */}
          {(tickets.length > 0 || event.pricing?.is_paid) && (
            <div className="rounded-2xl border border-wine-100 bg-wine-50/30 p-4" data-testid="event-pricing-summary">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-wine-600" />
                  <h3 className="text-sm font-bold text-navy">Tarifs actuels</h3>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={resyncRoles} className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline" data-testid="resync-roles-btn"><RefreshCw className="h-3 w-3" />Resynchroniser les statuts</button>
                  <button onClick={() => setTab("tickets")} className="text-xs font-semibold text-wine-700 hover:underline" data-testid="goto-tickets-tab">Modifier les billets →</button>
                </div>
              </div>
              {tickets.length > 0 ? (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {tickets.map((t) => (
                    <div key={t.id} className="rounded-xl border bg-white px-3 py-2 text-xs" style={{ borderColor: t.color || "#E33170" + "30" }} data-testid={`ticket-pill-${t.id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 font-bold text-neutral-900">
                          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color || "#722F37" }} />
                          {t.name}
                        </span>
                        <span className="font-bold text-wine-700">{(t.price || 0).toFixed(0)}&nbsp;€</span>
                      </div>
                      {(t.member_price != null && t.member_price !== t.price) && <div className="mt-0.5 text-[10px] text-emerald-700">Membre : {Number(t.member_price).toFixed(0)} €</div>}
                      {t.audience && <div className="mt-0.5 text-[10px] uppercase tracking-wider text-neutral-500">{t.audience}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-neutral-600">Tarif legacy : <strong>{event.pricing?.amount} {(event.pricing?.currency || 'eur').toUpperCase()}</strong></p>
              )}
            </div>
          )}

          {/* Toolbar: filter category + exports + add manual */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-neutral-500">Catégorie :</label>
              <select value={exportCategory} onChange={(e) => setExportCategory(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-1.5 text-xs" data-testid="export-cat">
                <option value="">Toutes</option>
                <option value="visitor">Visiteur</option>
                <option value="exhibitor">Exposant</option>
                <option value="media">Média</option>
                {tickets.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div className="flex flex-1 justify-end gap-2">
              <button type="button" onClick={() => downloadAttendees("csv")}
                 className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-4 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 cursor-pointer"
                 data-testid="export-csv">
                <FileSpreadsheet className="h-3.5 w-3.5" /> Exporter CSV
              </button>
              <button type="button" onClick={() => downloadAttendees("pdf")}
                 className="inline-flex items-center gap-1.5 rounded-full border border-wine-300 bg-wine-50 px-4 py-1.5 text-xs font-bold text-wine-700 hover:bg-wine-100 cursor-pointer"
                 data-testid="export-pdf">
                <FileText className="h-3.5 w-3.5" /> Catalogue PDF
              </button>
              <button onClick={() => setOffer({})} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="open-ticket-offer">🎟️ Créer un billet (lien de paiement)</button>
              <button onClick={() => setAddPart({ ...addPart, open: true })}
                      className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-600"
                      data-testid="open-add-participant">
                <UserPlus className="h-3.5 w-3.5" /> Ajouter
              </button>
              <button onClick={() => sendEventReminders("j7")} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-amber-600" data-testid="send-reminder-j7" title="Rappel J-7 à TOUS les participants : programme + catalogue + lien Stripe (régénéré pour non payés)">
                ⏰ Rappel J-7
              </button>
              <button onClick={() => sendEventReminders("j1")} className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-600" data-testid="send-reminder-j1" title="Rappel J-1 à TOUS les participants : dernière relance + badge + Stripe régénéré pour non payés">
                🚨 Rappel J-1
              </button>
              <button onClick={toggleEventCancelled} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-white ${event?.is_cancelled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-neutral-800 hover:bg-neutral-900"}`} data-testid="toggle-event-cancelled" title={event?.is_cancelled ? "Réactiver l'événement" : "Annuler l'événement et notifier les participants"}>
                {event?.is_cancelled ? "✓ Réactiver" : "🚫 Annuler l'événement"}
              </button>
            </div>
          </div>

          {addPart.open && (
            <form onSubmit={addParticipant} className="rounded-2xl border border-wine-200 bg-wine-50/30 p-5 grid gap-3 sm:grid-cols-2" data-testid="add-participant-form">
              <div className="sm:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="contact-picker">
                <label className="text-[10px] font-bold uppercase text-emerald-700">🔎 Sélectionner un contact existant (pré-remplit le formulaire)</label>
                <input
                  value={addPart.contactQuery || ""}
                  onChange={(e) => searchContactForInvite(e.target.value)}
                  placeholder="Chercher par nom, prénom ou email…"
                  className="mt-1 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs"
                  data-testid="contact-search"
                />
                {(addPart.contactResults || []).length > 0 && (
                  <ul className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-emerald-100 bg-white">
                    {addPart.contactResults.map((c) => (
                      <li key={c.id}>
                        <button type="button" onClick={() => pickContactForInvite(c)} className="block w-full px-3 py-2 text-left text-xs hover:bg-emerald-50" data-testid={`contact-pick-${c.id}`}>
                          <span className="font-bold">{c.first_name} {c.last_name}</span>
                          <span className="ml-2 text-neutral-500">{c.email}</span>
                          {c.linked_company_name && <span className="ml-2 rounded-full bg-wine-50 px-1.5 text-[10px] text-wine-700">{c.linked_company_name}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <input required placeholder="Prénom *" value={addPart.first_name} onChange={(e) => setAddPart({ ...addPart, first_name: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="ap-first" />
              <input required placeholder="Nom *" value={addPart.last_name} onChange={(e) => setAddPart({ ...addPart, last_name: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="ap-last" />
              <input required type="email" placeholder="Email *" value={addPart.email} onChange={(e) => setAddPart({ ...addPart, email: e.target.value })} className="sm:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="ap-email" />
              <input placeholder="Entreprise" value={addPart.company} onChange={(e) => setAddPart({ ...addPart, company: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input placeholder="Poste" value={addPart.job_title} onChange={(e) => setAddPart({ ...addPart, job_title: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input placeholder="Téléphone" value={addPart.phone} onChange={(e) => setAddPart({ ...addPart, phone: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <select value={addPart.participation_type} onChange={(e) => setAddPart({ ...addPart, participation_type: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                <option value="visitor">Visiteur</option>
                <option value="exhibitor">Exposant</option>
                <option value="media">Média</option>
              </select>
              {tickets.length > 0 && (
                <select value={addPart.ticket_id} onChange={(e) => setAddPart({ ...addPart, ticket_id: e.target.value })} className="sm:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                  <option value="">— Aucun ticket spécifique —</option>
                  {tickets.map((t) => <option key={t.id} value={t.id}>{`${t.name} (${t.price}€)`}</option>)}
                </select>
              )}

              {/* Lier à un membre (entreprise) — active la licence gratuite */}
              <div className="sm:col-span-2 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="invite-member-block">
                <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">🔗 Lier à une entreprise membre (option)</div>
                {addPart.member_company_id ? (
                  <div className="mt-2 flex items-center justify-between rounded-lg bg-white px-3 py-1.5 text-xs">
                    <span><Crown className="mr-1 inline h-3 w-3 text-wine-700" /> {addPart.company}</span>
                    <button type="button" onClick={() => setAddPart({ ...addPart, member_company_id: "", company: "" })} className="text-[10px] font-semibold text-rose-600 hover:underline">Délier</button>
                  </div>
                ) : (
                  <>
                    <input value={addPart.companyQuery || ""} onChange={(e) => searchCompanyForInvite(e.target.value)} placeholder="Chercher un membre…" className="mt-2 w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs" data-testid="invite-member-search" />
                    {(addPart.companyResults || []).length > 0 && (
                      <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-indigo-100 bg-white">
                        {addPart.companyResults.map((m) => (
                          <button key={m.id} type="button" onClick={() => pickCompanyForInvite(m)} className="block w-full border-t border-neutral-50 px-3 py-1.5 text-left text-[11px] hover:bg-indigo-50/60" data-testid={`invite-member-pick-${m.id}`}>
                            <span className="font-semibold">{m.name}</span> <span className="text-neutral-500">{m.city || ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="mt-1 text-[10px] italic text-indigo-600">Si un autre collaborateur a déjà payé pour cet événement, l&apos;inscription sera <strong>automatiquement gratuite</strong>.</p>
                  </>
                )}
              </div>
              <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-neutral-700">
                <input type="checkbox" checked={addPart.notify} onChange={(e) => setAddPart({ ...addPart, notify: e.target.checked })} className="accent-wine-500" />
                Envoyer l&apos;email d&apos;activation au participant
              </label>
              <div className="sm:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setAddPart({ ...addPart, open: false })} className="rounded-full bg-white border border-neutral-200 px-4 py-2 text-xs font-semibold">Annuler</button>
                <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="ap-submit">Ajouter le participant</button>
              </div>
            </form>
          )}

          {selectedAttendees.length > 0 && (
            <div className="flex items-center justify-between rounded-2xl border-2 border-wine-300 bg-wine-50/50 p-3" data-testid="bulk-toolbar">
              <span className="text-sm font-semibold text-wine-700">{selectedAttendees.length} participant{selectedAttendees.length > 1 ? "s" : ""} sélectionné{selectedAttendees.length > 1 ? "s" : ""}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelectedAttendees([])} className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold text-neutral-600 hover:bg-neutral-50" data-testid="bulk-clear">Désélectionner</button>
                <button onClick={sendCatalogToSelected} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-600" data-testid="bulk-send-catalog"><Send className="h-3 w-3" /> Envoyer le catalogue</button>
                <button onClick={() => openEmailModal("custom")} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-indigo-600" data-testid="bulk-send-email"><Send className="h-3 w-3" /> Envoyer un email</button>
                <button onClick={bulkDeleteAttendees} className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-700" data-testid="bulk-delete-attendees"><Trash2 className="h-3 w-3" /> Supprimer la sélection</button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
            <table className="w-full min-w-[1240px] text-sm">
              <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
                <tr><th className="px-3 py-3 w-8"><input type="checkbox" onChange={(e) => setSelectedAttendees(e.target.checked ? attendees.map((a) => a.id) : [])} checked={selectedAttendees.length === attendees.length && attendees.length > 0} data-testid="bulk-select-all" /></th><th className="px-5 py-3 text-left">Participant</th><th className="px-5 py-3 text-left">Société</th><th className="px-5 py-3 text-left">Rôle</th><th className="px-5 py-3 text-left">Billet</th><th className="px-5 py-3 text-left">Paiement</th><th className="px-5 py-3 text-left">Check-in</th><th className="px-5 py-3 text-right whitespace-nowrap">Actions</th></tr>
              </thead>
              <tbody>
                {attendees.filter((a) => !exportCategory || a.participation_type === exportCategory || a.ticket_name === exportCategory).map((a) => {
                  const stripeUrl = a.stripe?.checkout_url || a.checkout_url || null;
                  const payState = a.payment_status || (a.status === "confirmed" ? "paid" : (stripeUrl ? "pending" : "free"));
                  const payBadge = {
                    paid: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle className="h-3 w-3" />, label: "Payé" },
                    pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock className="h-3 w-3" />, label: "En attente" },
                    failed: { cls: "bg-rose-50 text-rose-700 border-rose-200", icon: <X className="h-3 w-3" />, label: "Échec" },
                    refunded: { cls: "bg-neutral-100 text-neutral-600 border-neutral-200", icon: <RefreshCw className="h-3 w-3" />, label: "Remboursé" },
                    free: { cls: "bg-sky-50 text-sky-700 border-sky-200", icon: <Tag className="h-3 w-3" />, label: "Gratuit" },
                    comp_admin: { cls: "bg-sky-50 text-sky-700 border-sky-200", icon: <Tag className="h-3 w-3" />, label: "Offert" },
                  }[payState] || { cls: "bg-neutral-100 text-neutral-600 border-neutral-200", icon: null, label: payState };
                  return (
                  <tr key={a.id} className="border-t border-neutral-100" data-testid={`attendee-${a.id}`}>
                    <td className="px-3 py-3 w-8"><input type="checkbox" checked={selectedAttendees.includes(a.id)} onChange={() => toggleSelected(a.id)} data-testid={`bulk-select-${a.id}`} /></td>
                    <td className="px-5 py-3"><div className="font-semibold">{a.user_name || `${a.first_name||""} ${a.last_name||""}`.trim()}</div><div className="text-xs text-neutral-500">{a.user_email}</div></td>
                    <td className="px-5 py-3 text-neutral-700">{a.company || a.company_name || "—"}</td>
                    <td className="px-5 py-3">
                      {(() => {
                        const role = (a.participation_type || "visitor").toLowerCase();
                        const RC = {
                          visitor:   { cls: "bg-sky-100 text-sky-800 border-sky-300", label: "Visiteur", icon: "👤" },
                          exhibitor: { cls: "bg-rose-100 text-rose-800 border-rose-300", label: "Exposant", icon: "🏢" },
                          media:     { cls: "bg-violet-100 text-violet-800 border-violet-300", label: "Média", icon: "📰" },
                          speaker:   { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Intervenant", icon: "🎤" },
                        }[role] || { cls: "bg-neutral-100 text-neutral-700 border-neutral-300", label: role, icon: "•" };
                        return (
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${RC.cls}`} data-testid={`role-${a.id}`}>
                            <span>{RC.icon}</span>{RC.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: a.ticket_color || (tickets.find((tk) => tk.id === a.ticket_id) || {}).color || "#722F37" }} data-testid={`ticket-tag-${a.id}`}>
                        {a.ticket_name || a.participation_type}
                      </span>
                      <div className="mt-1 text-[11px] text-neutral-500">{a.price != null ? `${Number(a.price).toFixed(0)} ${(a.currency || 'eur').toUpperCase()}` : "—"}</div>
                      {a.added_by_admin && <span className="mt-0.5 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">Manuel</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${payBadge.cls}`} data-testid={`payment-status-${a.id}`}>
                        {payBadge.icon}{payBadge.label}
                      </span>
                      {a.paid_at && <div className="mt-0.5 text-[10px] text-neutral-500">Le {new Date(a.paid_at).toLocaleDateString("fr")}</div>}
                      {a.balance_due > 0 && <div className="mt-0.5 text-[10px] font-bold text-amber-700" data-testid={`balance-due-${a.id}`}>Complément dû : {a.balance_due} €</div>}
                      <button type="button" onClick={() => setOffer({ registration: a })} className="mt-1 inline-flex items-center gap-1 rounded-full border border-navy/30 bg-white px-2 py-0.5 text-[10px] font-semibold text-navy hover:bg-navy/5" data-testid={`change-ticket-${a.id}`} title="Changer de billet, appliquer un montant / code promo et envoyer le lien">🎟️ Changer de billet</button>
                      {/* Stripe action row — visible whenever payment is not finalized and the ticket has a price. */}
                      {payState !== "paid" && (a.price || 0) > 0 && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {a.stripe_link_stale && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`stripe-stale-${a.id}`} title={a.stripe_link_stale_reason || "Le lien Stripe ne correspond plus au tarif actuel — régénérez-le"}>
                              ⚠️ Lien obsolète
                            </span>
                          )}
                          {stripeUrl ? (
                            <>
                              <button type="button" onClick={() => navigator.clipboard.writeText(stripeUrl).then(() => toast.success("Lien Stripe copié")).catch(() => toast.error("Copie impossible — ouvrez le lien via « Renvoyer » ou copiez-le depuis la fiche"))} className="inline-flex items-center gap-1 rounded-full border border-wine-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-wine-700 hover:bg-wine-50" data-testid={`copy-stripe-${a.id}`} title="Copier le lien Stripe actuel">
                                <Link2 className="h-3 w-3" /> Copier
                              </button>
                              <button type="button" onClick={() => resendStripeLink(a)} className="inline-flex items-center gap-1 rounded-full border border-navy/30 bg-white px-2 py-0.5 text-[10px] font-semibold text-navy hover:bg-navy/5" data-testid={`resend-stripe-${a.id}`} title="Renvoyer le lien actuel par email">
                                <Send className="h-3 w-3" /> Renvoyer
                              </button>
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-600" data-testid={`no-stripe-${a.id}`} title="Aucun lien Stripe — utilisez « Régénérer » pour en créer un">
                              Pas de lien
                            </span>
                          )}
                          <button type="button" onClick={() => regenerateStripeLink(a, false)} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${a.stripe_link_stale ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-wine-400 bg-white text-wine-700 hover:bg-wine-50"}`} data-testid={`regen-stripe-${a.id}`} title="Génère un nouveau lien Stripe et le copie dans le presse-papier">
                            🔄 Régénérer (copier)
                          </button>
                          <button type="button" onClick={() => regenerateStripeLink(a, true)} className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100" data-testid={`regen-send-stripe-${a.id}`} title="Génère un nouveau lien Stripe et l'envoie par email au participant">
                            🔄✉️ Régénérer &amp; envoyer
                          </button>
                          <button type="button" onClick={() => markRegistrationPaid(a.id, true)} className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-white px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50" data-testid={`mark-paid-${a.id}`} title="Marquer comme payé manuellement (virement, chèque, espèces)">
                            💶 Marquer payé
                          </button>
                        </div>
                      )}
                      {payState === "paid" && a.manual_payment && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" title={`Marqué payé manuellement par ${a.manual_payment_by || "admin"}${a.manual_payment_note ? ` — ${a.manual_payment_note}` : ""}`} data-testid={`manual-paid-${a.id}`}>
                            💶 Paiement manuel
                          </span>
                          <button type="button" onClick={() => markRegistrationPaid(a.id, false)} className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-700 hover:bg-neutral-100" data-testid={`unmark-paid-${a.id}`} title="Annuler le marquage paiement">
                            Annuler
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">{a.checked_in ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle className="h-3.5 w-3.5" /> Présent</span> : <span className="text-neutral-400">—</span>}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1 whitespace-nowrap">
                        {a.badge_code && <a href={`${BACKEND}/api/registrations/${a.id}/badge`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-wine-300 px-3 py-1 text-xs font-semibold text-wine-700 hover:bg-wine-50" data-testid={`badge-dl-${a.id}`}><Download className="h-3 w-3" /> Badge</a>}
                        <a href={`${BACKEND}/api/admin/events/${event.id}/registrations/${a.id}/badge.pdf?token=${encodeURIComponent(getAuthToken() || '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-navy/30 bg-navy/5 px-3 py-1 text-xs font-semibold text-navy hover:bg-navy/10" data-testid={`badge-pdf-${a.id}`} onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const r = await api.get(`/admin/events/${event.id}/registrations/${a.id}/badge.pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                            window.open(url, "_blank");
                          } catch (err) { toast.error("Erreur génération PDF"); }
                        }}><Download className="h-3 w-3" /> Badge PDF A4</a>
                        <button onClick={() => setEditingAttendee({ ...a, first_name: a.first_name || (a.user_name||"").split(" ")[0] || "", last_name: a.last_name || (a.user_name||"").split(" ").slice(1).join(" ") || "" })} className="grid h-7 w-7 place-items-center rounded-full text-navy hover:bg-navy/10" title="Modifier ce participant" data-testid={`attendee-edit-${a.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteAttendee(a)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer ce participant" data-testid={`attendee-del-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );})}
                {attendees.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-neutral-500">Aucun participant.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "tickets" && (
        <div className="mt-6 max-w-4xl rounded-2xl border border-wine-200 bg-white p-6" data-testid="tickets-form">
          <h3 className="text-xl font-bold text-navy inline-flex items-center gap-2"><Ticket className="h-5 w-5 text-wine-500" /> Types de tickets</h3>
          <p className="mt-1 text-sm text-neutral-600">Définissez vos billets (ex: Early Bird, Standard, VIP, Pass exposant…). Toute modification met à jour automatiquement les fiches participants <strong>en attente de paiement</strong>, ainsi que la couleur du tag sur les badges PDF.</p>

          <form onSubmit={async (e) => { e.preventDefault(); if (!newTicket.name) return; const PALETTE = ["#E33170","#263064","#722F37","#F59E0B","#10B981","#0EA5E9","#8B5CF6","#EC4899"]; const next = [...tickets, { ...newTicket, id: crypto.randomUUID(), color: newTicket.color || PALETTE[tickets.length % PALETTE.length] }]; setTickets(next); setNewTicket({ name: "", price: 0, description: "", role: "visitor", color: "", external_url: "" }); await saveTickets(null, next); }} className="mt-5 grid gap-2 sm:grid-cols-5">
            <input required placeholder="Nom du ticket" value={newTicket.name} onChange={(e) => setNewTicket({ ...newTicket, name: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="new-ticket-name" />
            <input required type="number" min="0" step="0.01" placeholder="Prix (€)" value={newTicket.price} onChange={(e) => setNewTicket({ ...newTicket, price: parseFloat(e.target.value) || 0 })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-ticket-price" />
            <select value={newTicket.role} onChange={(e) => setNewTicket({ ...newTicket, role: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
              <option value="visitor">Visiteur</option>
              <option value="exhibitor">Exposant</option>
              <option value="media">Média</option>
            </select>
            <button type="submit" className="rounded-xl bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="new-ticket-add">+ Ajouter</button>
            <input placeholder="Description (optionnel) — ex: Startups < 3 ans OU < 500 k€ CA" value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} className="sm:col-span-5 rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
            <input type="url" placeholder="🔗 Lien d'inscription externe (optionnel) — ex: https://forms.gle/xxx" value={newTicket.external_url} onChange={(e) => setNewTicket({ ...newTicket, external_url: e.target.value })} className="sm:col-span-5 rounded-xl border border-indigo-200 bg-indigo-50/40 px-3 py-2 text-sm" data-testid="new-ticket-external-url" />
          </form>

          <div className="mt-5 space-y-2">
            {tickets.length === 0 && <div className="rounded-xl bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">Aucun ticket défini.</div>}
            {tickets.map((t, i) => (
              <div key={t.id || i} className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-100 bg-neutral-50/40 p-3" data-testid={`ticket-${t.id || i}`}>
                <input type="color" value={t.color || "#722F37"} onChange={(e) => { const next = [...tickets]; next[i] = { ...t, color: e.target.value }; setTickets(next); }} className="h-9 w-12 cursor-pointer rounded-lg border border-neutral-200" title="Couleur du badge/tag" data-testid={`ticket-color-${t.id || i}`} />
                <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: t.color || "#722F37" }} data-testid={`ticket-tag-preview-${t.id || i}`}>{t.name || "Ticket"}</span>
                <input value={t.name} onChange={(e) => { const next = [...tickets]; next[i] = { ...t, name: e.target.value }; setTickets(next); }} className="flex-1 min-w-[140px] rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm font-semibold" data-testid={`ticket-name-${t.id || i}`} />
                <input type="number" min="0" step="0.01" value={t.price} onChange={(e) => { const next = [...tickets]; next[i] = { ...t, price: parseFloat(e.target.value) || 0 }; setTickets(next); }} className="w-24 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-right text-sm" data-testid={`ticket-price-${t.id || i}`} />
                <span className="text-xs text-neutral-500">€</span>
                <select value={t.role || "visitor"} onChange={(e) => { const next = [...tickets]; next[i] = { ...t, role: e.target.value }; setTickets(next); }} className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs">
                  <option value="visitor">Visiteur</option>
                  <option value="exhibitor">Exposant</option>
                  <option value="media">Média</option>
                </select>
                {t.external_url && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 border border-indigo-300 px-2 py-0.5 text-[10px] font-bold text-indigo-700" title={t.external_url} data-testid={`ticket-external-badge-${t.id || i}`}>
                    🔗 Externe
                  </span>
                )}
                <button onClick={async () => { const next = tickets.filter((_, idx) => idx !== i); setTickets(next); await saveTickets(null, next); }} className="grid h-8 w-8 place-items-center rounded-full text-rose-500 hover:bg-rose-50" data-testid={`ticket-remove-${t.id || i}`}><Trash2 className="h-4 w-4" /></button>
                {/* Editable fields on separate row for readability */}
                <div className="basis-full flex flex-wrap gap-2 pl-16 pt-1">
                  <input
                    placeholder="Description / critères d'éligibilité (optionnel)"
                    value={t.description || ""}
                    onChange={(e) => { const next = [...tickets]; next[i] = { ...t, description: e.target.value }; setTickets(next); }}
                    className="flex-1 min-w-[240px] rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs"
                    data-testid={`ticket-description-${t.id || i}`}
                  />
                  <input
                    type="url"
                    placeholder="🔗 Lien d'inscription externe (optionnel)"
                    value={t.external_url || ""}
                    onChange={(e) => { const next = [...tickets]; next[i] = { ...t, external_url: e.target.value }; setTickets(next); }}
                    className="flex-1 min-w-[240px] rounded-lg border border-indigo-200 bg-indigo-50/40 px-2.5 py-1.5 text-xs"
                    data-testid={`ticket-external-url-${t.id || i}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <button onClick={(e) => saveTickets(e)} className="mt-5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="tickets-save">💾 Enregistrer les modifications</button>
          <p className="mt-2 text-[10px] text-neutral-500">L'ajout et la suppression d'un ticket sont enregistrés automatiquement. Ce bouton sert à sauvegarder les modifications sur les tickets existants (prix, description, URL externe…).</p>
          <p className="mt-2 text-[11px] text-neutral-500">⚠️ La modification d&apos;un prix ne change pas le montant déjà encaissé sur Stripe (audit conservé), mais met à jour les inscriptions en attente.</p>

          {/* Sprint 92aj — Codes promo */}
          <div className="mt-10 rounded-2xl border border-pink-200 bg-pink-50/30 p-5" data-testid="promo-codes-admin">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-lg font-bold text-navy inline-flex items-center gap-2">🏷️ Codes promo</h3>
                <p className="mt-0.5 text-xs text-neutral-600">Créez des codes qui s'appliquent à <strong>une ou plusieurs catégories de tarifs</strong>. Laissez vide pour un code applicable à tous les billets.</p>
              </div>
            </div>

            <form onSubmit={async (ev) => {
              ev.preventDefault();
              const code = (newPromo.code || "").trim().toUpperCase();
              if (!code) { toast.error("Code requis"); return; }
              if (promoCodes.some((p) => (p.code || "").toUpperCase() === code)) { toast.error("Ce code existe déjà"); return; }
              const next = [...promoCodes, { ...newPromo, code, max_uses: newPromo.max_uses === "" ? null : parseInt(newPromo.max_uses, 10) || null, expires_at: newPromo.expires_at || null }];
              try {
                const { data } = await api.patch(`/admin/events/${id}/promo-codes`, { promo_codes: next });
                setPromoCodes(data.promo_codes);
                setNewPromo({ code: "", type: "percent", value: 10, ticket_ids: [], max_uses: "", expires_at: "", active: true });
                toast.success("Code promo ajouté");
              } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
            }} className="mt-4 grid gap-2 sm:grid-cols-6">
              <input required placeholder="CODE (ex: EARLY20)" value={newPromo.code} onChange={(ev) => setNewPromo({ ...newPromo, code: ev.target.value.toUpperCase() })} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm font-mono sm:col-span-2" data-testid="new-promo-code" />
              <select value={newPromo.type} onChange={(ev) => setNewPromo({ ...newPromo, type: ev.target.value })} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="new-promo-type">
                <option value="percent">% de remise</option>
                <option value="fixed">€ de remise</option>
              </select>
              <input required type="number" min="0" step="0.01" value={newPromo.value} onChange={(ev) => setNewPromo({ ...newPromo, value: parseFloat(ev.target.value) || 0 })} placeholder="Valeur" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="new-promo-value" />
              <input type="number" min="0" value={newPromo.max_uses} onChange={(ev) => setNewPromo({ ...newPromo, max_uses: ev.target.value })} placeholder="Max utilisations" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="new-promo-max" />
              <button type="submit" className="rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700" data-testid="new-promo-add">+ Ajouter</button>

              <div className="sm:col-span-3 flex items-center gap-2 text-xs">
                <span className="font-semibold text-neutral-700">S'applique aux tarifs :</span>
                <select
                  multiple
                  value={newPromo.ticket_ids}
                  onChange={(ev) => setNewPromo({ ...newPromo, ticket_ids: Array.from(ev.target.selectedOptions).map((o) => o.value) })}
                  className="flex-1 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs"
                  data-testid="new-promo-tickets"
                >
                  {tickets.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.price}€</option>)}
                </select>
                <span className="text-[10px] text-neutral-500 italic">Ctrl/Cmd + clic. Vide = tous.</span>
              </div>
              <input type="datetime-local" value={newPromo.expires_at} onChange={(ev) => setNewPromo({ ...newPromo, expires_at: ev.target.value })} className="sm:col-span-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="new-promo-expires" title="Date d'expiration (optionnel)" />
            </form>

            <div className="mt-5 space-y-2">
              {promoCodes.length === 0 && <p className="text-xs italic text-neutral-400">Aucun code promo pour cet événement.</p>}
              {promoCodes.map((p, i) => (
                <div key={p.id || i} className="flex flex-wrap items-center gap-2 rounded-xl border border-pink-200 bg-white p-3" data-testid={`promo-row-${p.id || i}`}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-pink-100 px-3 py-1 font-mono text-xs font-bold text-pink-700">{p.code}</span>
                  <span className="text-sm text-navy font-semibold">
                    {p.type === "percent" ? `-${p.value}%` : `-${p.value}€`}
                  </span>
                  <span className="text-xs text-neutral-600">
                    {(p.ticket_ids || []).length === 0
                      ? "Tous les tarifs"
                      : (p.ticket_ids || []).map((tid) => (tickets.find((t) => t.id === tid) || {}).name || "?").join(" · ")}
                  </span>
                  {p.max_uses && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-700">{p.used_count || 0}/{p.max_uses} util.</span>}
                  {!p.max_uses && p.used_count > 0 && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-700">{p.used_count} util.</span>}
                  {p.expires_at && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">⏰ {new Date(p.expires_at).toLocaleDateString("fr")}</span>}
                  <label className="ml-auto inline-flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={!!p.active} onChange={async (ev) => {
                      const next = promoCodes.map((pc, idx) => idx === i ? { ...pc, active: ev.target.checked } : pc);
                      setPromoCodes(next);
                      try { await api.patch(`/admin/events/${id}/promo-codes`, { promo_codes: next }); } catch (e) { toast.error("Erreur"); }
                    }} className="accent-pink-600" data-testid={`promo-active-${p.id || i}`} />
                    Actif
                  </label>
                  <button onClick={async () => {
                    if (!window.confirm(`Supprimer le code "${p.code}" ?`)) return;
                    const next = promoCodes.filter((_, idx) => idx !== i);
                    try {
                      const { data } = await api.patch(`/admin/events/${id}/promo-codes`, { promo_codes: next });
                      setPromoCodes(data.promo_codes);
                      toast.success("Code supprimé");
                    } catch (e) { toast.error("Erreur"); }
                  }} className="grid h-8 w-8 place-items-center rounded-full text-rose-500 hover:bg-rose-50" data-testid={`promo-remove-${p.id || i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Sprint 75 — Services additionnels (add-ons) */}
          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50/30 p-5" data-testid="addons-admin">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-navy">✨ Services additionnels (add-ons)</h3>
                <p className="mt-0.5 text-xs text-neutral-600">Options que les participants peuvent ajouter à leur billet (parking, restauration, accès VIP, etc.).</p>
              </div>
            </div>

            <form onSubmit={(ev) => { ev.preventDefault(); if (!newAddon.name) return; setAddons([...addons, { ...newAddon, id: crypto.randomUUID() }]); setNewAddon({ name: "", price: 0, description: "" }); }} className="mt-4 grid gap-2 sm:grid-cols-4">
              <input value={newAddon.name} onChange={(ev) => setNewAddon({ ...newAddon, name: ev.target.value })} placeholder="Nom du service" className="sm:col-span-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="addon-new-name" />
              <input type="number" min="0" step="0.01" value={newAddon.price} onChange={(ev) => setNewAddon({ ...newAddon, price: parseFloat(ev.target.value) || 0 })} placeholder="Prix (€)" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="addon-new-price" />
              <button type="submit" className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600" data-testid="addon-new-add">+ Ajouter</button>
              <input value={newAddon.description} onChange={(ev) => setNewAddon({ ...newAddon, description: ev.target.value })} placeholder="Description (optionnel)" className="sm:col-span-4 rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="addon-new-desc" />
            </form>

            <div className="mt-5 space-y-2">
              {addons.length === 0 && <p className="text-xs italic text-neutral-400">Aucun service additionnel pour cet événement.</p>}
              {addons.map((a, i) => (
                <div key={a.id || i} className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-white p-3" data-testid={`addon-row-${a.id || i}`}>
                  <input value={a.name} onChange={(ev) => { const next = [...addons]; next[i] = { ...a, name: ev.target.value }; setAddons(next); }} className="flex-1 min-w-[140px] rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm font-semibold" data-testid={`addon-name-${a.id || i}`} />
                  <input type="number" min="0" step="0.01" value={a.price} onChange={(ev) => { const next = [...addons]; next[i] = { ...a, price: parseFloat(ev.target.value) || 0 }; setAddons(next); }} className="w-24 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-right text-sm" data-testid={`addon-price-${a.id || i}`} />
                  <span className="text-xs text-neutral-500">€</span>
                  <input value={a.description || ""} onChange={(ev) => { const next = [...addons]; next[i] = { ...a, description: ev.target.value }; setAddons(next); }} placeholder="Description" className="basis-full sm:basis-auto sm:flex-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs" data-testid={`addon-desc-${a.id || i}`} />
                  <button onClick={() => setAddons(addons.filter((_, idx) => idx !== i))} className="grid h-8 w-8 place-items-center rounded-full text-rose-500 hover:bg-rose-50" data-testid={`addon-remove-${a.id || i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={saveAddons} className="mt-5 rounded-full bg-amber-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-600" data-testid="addons-save">Enregistrer les services</button>
          </div>
        </div>
      )}

      {tab === "checkin" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <form onSubmit={checkin} className="rounded-2xl border border-wine-200 bg-white p-6">
            <div className="grid h-16 w-16 place-items-center rounded-2xl bg-wine-500 text-white"><ScanLine className="h-8 w-8" /></div>
            <h3 className="mt-4 text-xl font-bold text-navy">Scanner un badge</h3>
            <p className="mt-1 text-sm text-neutral-600">Saisissez le code badge ou scannez le QR pour effectuer le check-in.</p>
            <input
              autoFocus
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              placeholder="WT-XXXXXXXX"
              className="mt-4 w-full rounded-xl border-2 border-wine-300 bg-white px-4 py-3 text-lg font-mono uppercase outline-none focus:border-wine-500"
              data-testid="checkin-input"
            />
            <button type="submit" className="mt-3 w-full rounded-full bg-wine-500 px-5 py-3 text-sm font-bold text-white hover:bg-wine-600" data-testid="checkin-submit"><QrCode className="inline h-4 w-4" /> Valider check-in</button>
          </form>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Présents</div>
            <div className="mt-2 text-6xl font-bold text-emerald-700">{presentCount}<span className="text-xl text-emerald-500">/{attendees.length}</span></div>
            <div className="mt-4 space-y-1.5 max-h-64 overflow-y-auto">
              {attendees.filter((a) => a.checked_in).map((a) => (
                <div key={a.id} className="text-xs"><CheckCircle className="mr-1 inline h-3 w-3 text-emerald-600" /> {a.user_name} <span className="text-neutral-500">({a.company || "—"})</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
      {tab === "agenda" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_2fr]">
          <form onSubmit={addSlot} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-5" data-testid="agenda-form">
            <h3 className="font-bold text-navy">Ajouter un créneau</h3>
            <input required value={newSlot.title} onChange={(e) => setNewSlot({ ...newSlot, title: e.target.value })} placeholder="Titre (ex: Table ronde IA & Vin)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="agenda-title" />
            <select value={newSlot.animation_type} onChange={(e) => setNewSlot({ ...newSlot, animation_type: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="agenda-type">
              {Object.entries(ANIMATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input required type="datetime-local" value={newSlot.start_time} onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })} className="rounded-xl border border-neutral-200 px-2 py-2 text-xs" data-testid="agenda-start" />
              <input required type="datetime-local" value={newSlot.end_time} onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })} className="rounded-xl border border-neutral-200 px-2 py-2 text-xs" data-testid="agenda-end" />
            </div>
            <input value={newSlot.location} onChange={(e) => setNewSlot({ ...newSlot, location: e.target.value })} placeholder="Lieu / salle" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="agenda-location" />
            <input type="number" min="0" value={newSlot.capacity} onChange={(e) => setNewSlot({ ...newSlot, capacity: parseInt(e.target.value) || 0 })} placeholder="Capacité (0 = illimitée)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="agenda-capacity" />

            {newSlot.animation_type === "speed_meeting" && (
              <div className="rounded-xl border border-dashed border-wine-200 bg-wine-50/30 p-3" data-testid="agenda-sm-config">
                <div className="text-xs font-bold text-wine-700">Paramètres RDV 1:1 individuel</div>
                <p className="mt-1 text-[11px] text-neutral-600">
                  Un RDV 1:1 individuel est une série de rendez-vous courts (5–15 min) entre des participants à un même créneau global.
                  Définissez la <strong>durée d&apos;un rendez-vous individuel</strong> et les rôles qui peuvent se rencontrer (ex: Exposant ↔ Visiteur).
                </p>
                <div className="mt-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Rôles participants</div>
                  <p className="text-[10px] italic text-neutral-500">Au moins 2 rôles différents qui se rencontreront pendant les RDV.</p>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {newSlot.sm_roles.map((r, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full bg-wine-100 px-2 py-0.5 text-xs font-semibold text-wine-700">
                        {r}
                        <button type="button" onClick={() => setNewSlot({ ...newSlot, sm_roles: newSlot.sm_roles.filter((_, idx) => idx !== i) })} className="text-wine-600 hover:text-red-600">×</button>
                      </span>
                    ))}
                    <button type="button" onClick={addRole} className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-wine-700 ring-1 ring-wine-300 hover:bg-wine-50" data-testid="agenda-sm-add-role">+ Rôle</button>
                  </div>
                </div>
                <label className="mt-3 block text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                  Durée d&apos;un rendez-vous individuel (en minutes)
                  <span className="block text-[10px] font-normal italic text-neutral-500">Exemple : 10 → chaque participant aura des rendez-vous d&apos;exactement 10 minutes. Plage conseillée : 5 à 15 min.</span>
                  <input type="number" min="3" max="60" value={newSlot.sm_slot_duration} onChange={(e) => setNewSlot({ ...newSlot, sm_slot_duration: parseInt(e.target.value) || 10 })} className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-xs font-normal" data-testid="agenda-sm-duration" />
                </label>
              </div>
            )}

            <textarea rows={2} value={newSlot.description} onChange={(e) => setNewSlot({ ...newSlot, description: e.target.value })} placeholder="Description courte" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-xs" data-testid="agenda-description" />
            <button type="submit" className="w-full rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="agenda-add"><Plus className="inline h-3 w-3" /> Ajouter</button>
          </form>

          <div className="space-y-3" data-testid="agenda-list">
            <p className="text-[11px] italic text-neutral-500">💡 Glissez-déposez les créneaux pour réorganiser le programme.</p>
            {agenda.map((s) => {
              const startD = new Date(s.start_time);
              const endD = new Date(s.end_time);
              return (
                <div
                  key={s.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, s.id)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, s.id)}
                  className={`rounded-2xl border bg-white p-4 transition-shadow ${dragSlotId === s.id ? "opacity-50 border-wine-400 shadow-lg" : "border-neutral-100 hover:border-wine-200 hover:shadow-sm"}`}
                  data-testid={`agenda-slot-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <span className="mt-1 cursor-grab text-neutral-300 hover:text-wine-500 select-none" title="Glisser pour réordonner" data-testid={`agenda-drag-${s.id}`}>⋮⋮</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-wine-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-wine-700">{ANIMATIONS[s.animation_type] || s.animation_type}</span>
                          {s.capacity > 0 && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-600">{s.capacity} pl.</span>}
                        </div>
                        <h4 className="mt-1 font-bold text-navy">{s.title}</h4>
                        <div className="mt-1 text-xs text-neutral-600">
                          <Clock className="mr-1 inline h-3 w-3" /> {startD.toLocaleString("fr", { dateStyle: "short", timeStyle: "short" })} → {endD.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}
                          {s.location && <> · 📍 {s.location}</>}
                        </div>
                        {s.description && <p className="mt-1.5 text-xs text-neutral-600">{s.description}</p>}
                        {s.animation_type === "speed_meeting" && s.sm_roles?.length > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Rôles :</span>
                            {s.sm_roles.map((r, i) => <span key={i} className="rounded-full bg-wine-100 px-2 py-0.5 text-[10px] font-semibold text-wine-700">{r}</span>)}
                            <span className="text-[10px] text-neutral-500">· {s.sm_slot_duration} min/RDV</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => startEdit(s)} className="rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy hover:bg-navy/10" data-testid={`agenda-edit-${s.id}`} title="Modifier le créneau"><Edit3 className="inline h-3 w-3" /></button>
                      <button onClick={() => delSlot(s.id)} className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-100" data-testid={`agenda-del-${s.id}`}><Trash2 className="inline h-3 w-3" /></button>
                    </div>
                  </div>
                  {s.animation_type === "speed_meeting" && (
                    <div className="mt-4 border-t border-neutral-100 pt-3" data-testid={`exh-block-${s.id}`}>
                      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">Exposants ({(exhibitorsBySlot[s.id] || []).length})</div>
                      {(exhibitorsBySlot[s.id] || []).length === 0 ? (
                        <p className="text-xs text-neutral-500">Aucun exposant pour le moment. Les membres peuvent s&apos;inscrire depuis la page publique de l&apos;événement.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(exhibitorsBySlot[s.id] || []).map((ex) => {
                            const exPaid = ex.payment_status === "paid" || ex.payment_status === "not_required";
                            return (
                            <div key={ex.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-wine-50/40 px-3 py-2 text-xs" data-testid={`exh-row-${ex.id}`}>
                              <span className="font-semibold text-navy">{ex.company_name}</span>
                              {ex.role && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-wine-700">{ex.role}</span>}
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ex.status === "approved" ? "bg-emerald-100 text-emerald-700" : ex.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{ex.status}</span>
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${exPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`} title={ex.manual_payment ? `Marqué payé manuellement par ${ex.manual_payment_by || "admin"}${ex.manual_payment_note ? ` — ${ex.manual_payment_note}` : ""}` : undefined} data-testid={`exh-pay-${ex.id}`}>
                                {exPaid ? "✅ Payé" : "💳 Paiement en attente"}
                                {ex.manual_payment && " (manuel)"}
                              </span>
                              <div className="ml-auto flex flex-wrap gap-1">
                                {!exPaid && <button onClick={() => markExhibitorPaid(ex.id, true)} className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800 hover:bg-emerald-200" data-testid={`exh-mark-paid-${ex.id}`}>Marquer payé</button>}
                                {exPaid && ex.manual_payment && <button onClick={() => markExhibitorPaid(ex.id, false)} className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-[10px] font-bold text-neutral-700 hover:bg-neutral-200" data-testid={`exh-unmark-paid-${ex.id}`}>Annuler paiement</button>}
                                {ex.status !== "approved" && <button onClick={() => moderateExhibitor(s.id, ex.id, "approved")} className="rounded-full bg-emerald-500 px-2.5 py-0.5 text-[10px] font-bold text-white hover:bg-emerald-600" data-testid={`exh-approve-${ex.id}`}>Valider</button>}
                                {ex.status !== "rejected" && <button onClick={() => moderateExhibitor(s.id, ex.id, "rejected")} className="rounded-full bg-red-100 px-2.5 py-0.5 text-[10px] font-bold text-red-700 hover:bg-red-200" data-testid={`exh-reject-${ex.id}`}>Refuser</button>}
                                <button onClick={() => deleteExhibitor(ex.id)} className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600 hover:bg-neutral-200" data-testid={`exh-del-${ex.id}`}><Trash2 className="inline h-2.5 w-2.5" /></button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {agenda.length === 0 && <div className="rounded-2xl border border-dashed border-neutral-200 p-8 text-center text-sm text-neutral-500">Aucun créneau au programme. Ajoutez le premier ↑</div>}
          </div>
        </div>
      )}

      {/* Sprint 92aj — Tarification tab removed (doublon de Tarifs/Tickets).
          Legacy pricing fields are still kept in the DB and visible read-only
          via the Tarifs summary card. */}

      {tab === "transactions" && (
        <div className="mt-6 space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Revenus encaissés</div>
              <div className="mt-1 text-3xl font-bold text-emerald-700" data-testid="revenue-total">
                {revenue.totals.length === 0 ? "0 €" : revenue.totals.map((t) => fmtMoney(t.amount, t.currency)).join(" + ")}
              </div>
            </div>
            <div className="rounded-2xl border border-wine-100 bg-white p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Paiements réussis</div>
              <div className="mt-1 text-3xl font-bold text-wine-700" data-testid="revenue-paid-count">{revenue.paid_count}</div>
            </div>
            <div className="rounded-2xl border border-neutral-100 bg-white p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Transactions totales</div>
              <div className="mt-1 text-3xl font-bold text-navy">{transactions.length}</div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
            <table className="w-full min-w-[1000px] text-sm">
              <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
                <tr><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Participant</th><th className="px-5 py-3 text-left">Type</th><th className="px-5 py-3 text-right">Montant</th><th className="px-5 py-3 text-left">Statut</th><th className="px-5 py-3 text-left">Session Stripe</th><th className="px-5 py-3 text-right">Action</th></tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-t border-neutral-100" data-testid={`tx-${t.id}`}>
                    <td className="px-5 py-3 text-xs text-neutral-500">{new Date(t.created_at).toLocaleString("fr")}</td>
                    <td className="px-5 py-3"><div className="font-semibold">{t.user_name}</div><div className="text-xs text-neutral-500">{t.user_email}</div></td>
                    <td className="px-5 py-3"><span className="rounded-full bg-wine-50 px-2 py-0.5 text-xs font-semibold text-wine-700">{t.participation_type || "—"}</span></td>
                    <td className="px-5 py-3 text-right font-bold">{fmtMoney(t.amount, t.currency)}</td>
                    <td className="px-5 py-3">
                      {t.payment_status === "paid" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle className="h-3 w-3" /> Payé</span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">En attente</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-[11px] text-neutral-500">{(t.session_id || "").slice(0, 24)}…</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => deleteTransaction(t)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer cette transaction" data-testid={`tx-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-neutral-500">Aucune transaction pour cet événement.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "budget" && <BudgetPanel eventId={id} />}
      {tab === "space" && <EventSpaceAdminPanel eventId={id} />}
      {tab === "meetings" && <EventMeetingsAdmin eventId={id} />}
      {offer && event && <TicketOfferModal event={{ ...event, id }} registration={offer.registration} onClose={() => setOffer(null)} onDone={load} />}

      {/* Edit a single agenda slot */}
      {editingAttendee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="attendee-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setEditingAttendee(null)} aria-label="Fermer" />
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-600" /> Modifier le participant</h2>
              <button onClick={() => setEditingAttendee(null)} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input value={editingAttendee.first_name} onChange={(e) => setEditingAttendee({ ...editingAttendee, first_name: e.target.value })} placeholder="Prénom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-attendee-first" />
                <input value={editingAttendee.last_name} onChange={(e) => setEditingAttendee({ ...editingAttendee, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-attendee-last" />
              </div>
              <input value={editingAttendee.company || ""} onChange={(e) => setEditingAttendee({ ...editingAttendee, company: e.target.value })} placeholder="Société (commercial)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={editingAttendee.job_title || ""} onChange={(e) => setEditingAttendee({ ...editingAttendee, job_title: e.target.value })} placeholder="Fonction" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={editingAttendee.linkedin || ""} onChange={(e) => setEditingAttendee({ ...editingAttendee, linkedin: e.target.value })} placeholder="LinkedIn (URL ou identifiant)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Rôle</label>
                <select value={editingAttendee.participation_type || "visitor"} onChange={(e) => setEditingAttendee({ ...editingAttendee, participation_type: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-attendee-role">
                  <option value="visitor">👤 Visiteur</option>
                  <option value="exhibitor">🏢 Exposant</option>
                  <option value="media">📰 Média</option>
                  <option value="speaker">🎤 Intervenant</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Billet</label>
                <select value={editingAttendee.ticket_id || ""} onChange={(e) => { const tk = tickets.find((x) => x.id === e.target.value); setEditingAttendee({ ...editingAttendee, ticket_id: e.target.value || null, ...(tk?.role ? { participation_type: tk.role } : {}) }); }} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-attendee-ticket">
                  <option value="">— Aucun (gratuit / hors billet) —</option>
                  {tickets.map((tk) => <option key={tk.id} value={tk.id}>{tk.name} — {tk.price} € · {tk.role}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-neutral-500">Changer le billet met à jour automatiquement le tarif, la couleur du tag et le rôle (modifiable ensuite ci-dessus).</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditingAttendee(null)} disabled={savingAttendee} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-50">Annuler</button>
              <button onClick={saveAttendee} disabled={savingAttendee} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="edit-attendee-save"><Save className="h-4 w-4" /> {savingAttendee ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit a single agenda slot */}
      {editingSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="agenda-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setEditingSlot(null)} aria-label="Fermer" />
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-600" /> Modifier le créneau</h2>
              <button onClick={() => setEditingSlot(null)} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <input value={editingSlot.title} onChange={(e) => setEditingSlot({ ...editingSlot, title: e.target.value })} placeholder="Titre" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-slot-title" />
              <select value={editingSlot.animation_type} onChange={(e) => setEditingSlot({ ...editingSlot, animation_type: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-slot-type">
                {Object.entries(ANIMATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="datetime-local" value={editingSlot.start_time} onChange={(e) => setEditingSlot({ ...editingSlot, start_time: e.target.value })} className="rounded-xl border border-neutral-200 px-2 py-2 text-xs" data-testid="edit-slot-start" />
                <input type="datetime-local" value={editingSlot.end_time} onChange={(e) => setEditingSlot({ ...editingSlot, end_time: e.target.value })} className="rounded-xl border border-neutral-200 px-2 py-2 text-xs" data-testid="edit-slot-end" />
              </div>
              <input value={editingSlot.location || ""} onChange={(e) => setEditingSlot({ ...editingSlot, location: e.target.value })} placeholder="Lieu / salle" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input type="number" min="0" value={editingSlot.capacity || 0} onChange={(e) => setEditingSlot({ ...editingSlot, capacity: parseInt(e.target.value) || 0 })} placeholder="Capacité (0 = illimitée)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <textarea rows={3} value={editingSlot.description || ""} onChange={(e) => setEditingSlot({ ...editingSlot, description: e.target.value })} placeholder="Description" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              {editingSlot.animation_type === "speed_meeting" && (
                <div className="rounded-xl border border-dashed border-wine-200 bg-wine-50/30 p-3 space-y-2">
                  <div className="text-xs font-bold text-wine-700">Paramètres RDV 1:1 individuel</div>
                  <input type="number" min="3" max="60" value={editingSlot.sm_slot_duration || 10} onChange={(e) => setEditingSlot({ ...editingSlot, sm_slot_duration: parseInt(e.target.value) || 10 })} className="w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" placeholder="Durée RDV (min)" />
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditingSlot(null)} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveEdit} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="edit-slot-save"><Save className="h-4 w-4" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit event modal — admin can edit even after publication */}
      {editingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="event-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setEditingEvent(null)} aria-label="Fermer" />
          <div className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-600" /> Modifier l&apos;événement</h2>
              <button onClick={() => setEditingEvent(null)} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Titre *</label>
                <input value={editingEvent.title} onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-event-title" />
              </div>
              {/* Sprint 92aa — Editable slug (URL) — auto-regenerated from title
                  when the event is a draft, but ALWAYS editable manually. */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">
                  🔗 URL personnalisée (slug)
                  <span className="ml-2 font-normal normal-case text-neutral-400">— visible sur : lawinetech.com/events/<strong>{editingEvent.slug || "…"}</strong></span>
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    value={editingEvent.slug}
                    onChange={(e) => setEditingEvent({ ...editingEvent, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "") })}
                    className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono"
                    placeholder="wine-paris-2027"
                    data-testid="edit-event-slug"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const auto = (editingEvent.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
                      setEditingEvent({ ...editingEvent, slug: auto });
                    }}
                    className="rounded-xl border border-wine-300 bg-wine-50 px-3 py-2 text-[11px] font-bold text-wine-700 hover:bg-wine-100"
                    title="Regénérer depuis le titre"
                    data-testid="edit-event-slug-regen"
                  >
                    ↻ depuis le titre
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-neutral-500">Seulement des lettres minuscules, chiffres et tirets. Modifier cette valeur peut casser des liens externes déjà partagés.</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Description</label>
                <textarea rows={4} value={editingEvent.description} onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Date & heure</label>
                <input type="datetime-local" value={editingEvent.date} onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Type</label>
                <input value={editingEvent.type} onChange={(e) => setEditingEvent({ ...editingEvent, type: e.target.value })} placeholder="Conference, Webinar, Coffee…" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Lieu / Adresse (Google Places)</label>
                <GooglePlacesInput
                  value={editingEvent.location || ""}
                  placeholder="Ex: 12 rue de la République, Narbonne"
                  testId="edit-event-location"
                  onSelect={(p) => setEditingEvent({
                    ...editingEvent,
                    location: p.address || editingEvent.location,
                    address: p.address || editingEvent.address,
                    city: p.city || editingEvent.city,
                    country: p.country || editingEvent.country,
                    google_place_id: p.place_id || editingEvent.google_place_id,
                  })}
                />
                <p className="mt-1 text-[11px] text-neutral-500">Tapez pour rechercher → la ville et le pays se remplissent automatiquement.</p>
              </div>
              <input value={editingEvent.city} onChange={(e) => setEditingEvent({ ...editingEvent, city: e.target.value })} placeholder="Ville" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={editingEvent.country} onChange={(e) => setEditingEvent({ ...editingEvent, country: e.target.value })} placeholder="Pays" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <select value={editingEvent.format} onChange={(e) => setEditingEvent({ ...editingEvent, format: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                <option value="physical">Physique</option>
                <option value="virtual">En ligne</option>
                <option value="hybrid">Hybride</option>
              </select>
              <select value={editingEvent.audience} onChange={(e) => setEditingEvent({ ...editingEvent, audience: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                <option value="public">Public</option>
                <option value="members_only">Membres uniquement</option>
              </select>
              <select value={editingEvent.status} onChange={(e) => setEditingEvent({ ...editingEvent, status: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="cancelled">Annulé</option>
              </select>
              <select value={editingEvent.lang} onChange={(e) => setEditingEvent({ ...editingEvent, lang: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-event-lang" title="Langue de l'événement (utilisée pour les badges, emails, etc.)">
                <option value="fr">🇫🇷 Français</option>
                <option value="en">🇬🇧 English</option>
                <option value="es">🇪🇸 Español</option>
                <option value="it">🇮🇹 Italiano</option>
                <option value="pt">🇵🇹 Português</option>
              </select>
              <input type="url" value={editingEvent.virtual_link} onChange={(e) => setEditingEvent({ ...editingEvent, virtual_link: e.target.value })} placeholder="Lien visio (si format en ligne)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm md:col-span-2" />
              <div className="md:col-span-2">
                <ImageUpload value={editingEvent.image} onChange={(url) => setEditingEvent({ ...editingEvent, image: url })} label="Image / bannière de l'événement" testId="edit-event-image" />
              </div>
            </div>
            <details className="mt-5 rounded-xl border border-neutral-100 bg-neutral-50 p-3" data-testid="event-diff-history-section">
              <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-neutral-600">Historique des modifications</summary>
              <div className="mt-3">
                <DiffHistory entityType="event" entityId={event.id} />
              </div>
            </details>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditingEvent(null)} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveEvent} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="edit-event-save"><Save className="h-3.5 w-3.5" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Notify contacts modal (Sprint 73) */}
      {notifyOpen && event && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setNotifyOpen(false)}>
          <form onSubmit={sendNotify} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-notify-modal">
            <h3 className="flex items-center gap-2 text-xl font-bold text-navy"><Bell className="h-5 w-5 text-wine-500" />Notifier des contacts</h3>
            <p className="mt-1 text-xs text-neutral-500">Promouvoir « {event.title} » auprès de contacts choisis ou par emails libres.</p>
            <div className="mt-4 relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-neutral-400" />
              <input value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} placeholder="Rechercher un contact…" className="w-full rounded-xl border border-neutral-200 pl-8 pr-3 py-2 text-sm" data-testid="event-notify-search" />
            </div>
            <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-neutral-100">
              {contactsLoading ? <div className="p-3 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-wine-500" /></div>
                : filteredNotifyContacts.length === 0 ? <div className="p-3 text-center text-xs text-neutral-500">Aucun contact</div>
                : filteredNotifyContacts.slice(0, 60).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 border-b border-neutral-100 px-3 py-1.5 text-xs hover:bg-wine-50/30 cursor-pointer">
                    <input type="checkbox" checked={selContacts.has(c.id)} onChange={() => toggleNotifyContact(c.id)} className="h-3.5 w-3.5" data-testid={`event-notify-contact-${c.id}`} />
                    <span className="flex-1 truncate"><strong>{c.first_name || ""} {c.last_name || ""}</strong> <span className="text-neutral-500">— {c.email}</span> {c.company_name && <em className="text-neutral-400">@ {c.company_name}</em>}</span>
                  </label>
                ))}
            </div>
            <div className="mt-1 text-[11px] text-neutral-500">{selContacts.size} contact{selContacts.size > 1 ? "s" : ""} sélectionné{selContacts.size > 1 ? "s" : ""}.</div>

            {/* Sprint 92x — Segment targeting: tags, poles, all newsletter */}
            <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3" data-testid="event-notify-segments">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">🎯 Cibler par segment (facultatif)</div>
                {(notifyTags.size + notifyPoles.size + (notifyAllNewsletter ? 1 : 0)) > 0 && (
                  <button type="button" onClick={() => { setNotifyTags(new Set()); setNotifyPoles(new Set()); setNotifyAllNewsletter(false); }} className="text-[10px] font-semibold text-indigo-600 hover:underline" data-testid="event-notify-segments-clear">Effacer</button>
                )}
              </div>

              {/* All newsletter opt-in */}
              <label className="mt-2 flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 border border-indigo-200 cursor-pointer hover:bg-indigo-50">
                <input type="checkbox" checked={notifyAllNewsletter} onChange={(e) => setNotifyAllNewsletter(e.target.checked)} className="h-3.5 w-3.5" data-testid="event-notify-all-newsletter" />
                <span className="text-xs font-semibold text-neutral-800">📧 Tous les abonnés newsletter (opt-in)</span>
              </label>

              {/* Pôles */}
              <div className="mt-2">
                <div className="text-[10px] font-semibold text-indigo-600 mb-1">Par pôle :</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { v: "producer", l: "🍇 Producteur" },
                    { v: "vintech", l: "⚙️ VinTech" },
                    { v: "distributor", l: "🚚 Distributeur" },
                    { v: "service", l: "🤝 Prestataire" },
                    { v: "institutional", l: "🏛️ Institutionnel" },
                    { v: "on_trade", l: "🍽️ CHR / On-trade" },
                  ].map((p) => (
                    <button
                      key={p.v}
                      type="button"
                      onClick={() => toggleNotifyPole(p.v)}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition ${notifyPoles.has(p.v) ? "bg-indigo-600 text-white" : "bg-white text-neutral-700 border border-neutral-200 hover:border-indigo-300"}`}
                      data-testid={`event-notify-pole-${p.v}`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags (top 20 by count) */}
              {availableTags.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-semibold text-indigo-600 mb-1">Par tag (top 20 par volume) :</div>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {availableTags.slice(0, 20).map((t) => (
                      <button
                        key={t.tag}
                        type="button"
                        onClick={() => toggleNotifyTag(t.tag)}
                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition ${notifyTags.has(t.tag) ? "bg-wine-600 text-white" : "bg-white text-neutral-700 border border-neutral-200 hover:border-wine-300"}`}
                        data-testid={`event-notify-tag-${t.tag}`}
                        title={t.label}
                      >
                        {t.label || t.tag} <span className="opacity-60">{t.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">+ Emails libres (séparés par , ou ;)</label>
            <input value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)} placeholder="alice@example.com, bob@example.com" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="event-notify-extra-emails" />
            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Message d&apos;intro (optionnel)</label>
            <textarea rows={3} value={notifyMsg} onChange={(e) => setNotifyMsg(e.target.value)} placeholder="Texte d'introduction personnalisé…" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="event-notify-message" />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setNotifyOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={notifySending} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="event-notify-send">
                {notifySending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer la notification
              </button>
            </div>
          </form>
        </div>
      )}
      {emailOpen && (
        <EventBulkEmailModal count={selectedAttendees.length} template={emailTemplate} onTemplate={(k) => openEmailModal(k)}
          subject={emailSubject} setSubject={setEmailSubject} body={emailBody} setBody={setEmailBody}
          sending={emailSending} onSubmit={sendBulkEmail} onClose={() => setEmailOpen(false)} />
      )}

    </PortalLayout>
  );
}


// =============================================================================
// Budget panel — revenue (tickets) + manual expense lines + auto margin
// =============================================================================
const EXPENSE_CATEGORIES = ["Lieu / location", "Restauration / catering", "Communication", "Logistique", "Personnel / prestataire", "Matériel", "Transport", "Autre"];

function BudgetPanel({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: "", amount: "", category: EXPENSE_CATEGORIES[0], date: new Date().toISOString().slice(0, 10), notes: "" });
  const [editingExp, setEditingExp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/events/${eventId}/budget`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur de chargement du budget");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [eventId]);

  const addExpense = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.amount) {
      toast.error("Libellé et montant requis");
      return;
    }
    try {
      await api.post(`/admin/events/${eventId}/budget/expenses`, {
        label: form.label.trim(),
        amount: parseFloat(form.amount),
        category: form.category,
        date: form.date,
        notes: form.notes,
      });
      setForm({ label: "", amount: "", category: EXPENSE_CATEGORIES[0], date: new Date().toISOString().slice(0, 10), notes: "" });
      toast.success("Dépense ajoutée");
      load();
    } catch (err) { toast.error("Erreur"); }
  };
  const deleteExpense = async (exp) => {
    if (!window.confirm(`Supprimer "${exp.label}" (${exp.amount} €) ?`)) return;
    try {
      await api.delete(`/admin/events/${eventId}/budget/expenses/${exp.id}`);
      toast.success("Dépense supprimée");
      load();
    } catch (err) { toast.error("Erreur"); }
  };
  const saveEditExpense = async () => {
    try {
      await api.patch(`/admin/events/${eventId}/budget/expenses/${editingExp.id}`, {
        label: editingExp.label,
        amount: parseFloat(editingExp.amount),
        category: editingExp.category,
        date: editingExp.date,
        notes: editingExp.notes,
      });
      setEditingExp(null);
      toast.success("Dépense modifiée");
      load();
    } catch (err) { toast.error("Erreur"); }
  };

  if (loading) return <div className="mt-6 text-sm text-neutral-500" data-testid="budget-loading">Chargement du budget…</div>;
  if (!data) return null;

  const marginPositive = data.margin >= 0;

  return (
    <div className="mt-6 space-y-6" data-testid="budget-panel">
      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5" data-testid="budget-revenue">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Chiffre d&apos;affaires</div>
          <div className="mt-1 text-3xl font-bold text-emerald-700">{data.revenue.toLocaleString("fr")} €</div>
          <div className="mt-1 text-[11px] text-emerald-600">{data.tickets_sold} billet{data.tickets_sold > 1 ? "s" : ""} payé{data.tickets_sold > 1 ? "s" : ""}{data.free_count > 0 ? ` · ${data.free_count} gratuit${data.free_count > 1 ? "s" : ""}` : ""}</div>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-5" data-testid="budget-expenses-total">
          <div className="text-xs font-bold uppercase tracking-wider text-rose-700">Dépenses</div>
          <div className="mt-1 text-3xl font-bold text-rose-700">{data.total_expenses.toLocaleString("fr")} €</div>
          <div className="mt-1 text-[11px] text-rose-600">{data.expenses.length} ligne{data.expenses.length > 1 ? "s" : ""}</div>
        </div>
        <div className={`rounded-2xl border p-5 ${marginPositive ? "border-wine-200 bg-wine-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid="budget-margin">
          <div className={`text-xs font-bold uppercase tracking-wider ${marginPositive ? "text-wine-700" : "text-amber-700"}`}>Marge nette</div>
          <div className={`mt-1 text-3xl font-bold ${marginPositive ? "text-wine-700" : "text-amber-700"}`}>{data.margin.toLocaleString("fr")} €</div>
          <div className={`mt-1 text-[11px] ${marginPositive ? "text-wine-600" : "text-amber-600"}`}>{data.margin_pct}% du CA</div>
        </div>
      </div>

      {/* Revenue breakdown by ticket */}
      {data.by_ticket.length > 0 && (
        <div className="rounded-2xl border border-emerald-100 bg-white p-5" data-testid="budget-by-ticket">
          <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-700">Détail recettes par billet</h3>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {data.by_ticket.map((tk) => (
              <div key={tk.name} className="flex items-center justify-between rounded-xl bg-emerald-50/40 px-4 py-2 text-sm">
                <div>
                  <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: tk.color }} /> <strong>{tk.name}</strong>
                  <span className="ml-2 text-xs text-neutral-500">{tk.count} × {tk.unit_price} €</span>
                </div>
                <div className="font-bold text-emerald-700">{tk.subtotal.toLocaleString("fr")} €</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add expense form */}
      <form onSubmit={addExpense} className="rounded-2xl border border-wine-100 bg-white p-5" data-testid="budget-add-form">
        <h3 className="text-sm font-bold uppercase tracking-wider text-wine-700">Ajouter une dépense</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_1fr_1fr_1fr_auto]">
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Libellé (ex: Location salle Narbonne)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="budget-input-label" required />
          <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Montant (€)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="budget-input-amount" required />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="budget-input-category">
            {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="budget-input-date" />
          <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="budget-add-btn"><Plus className="inline h-3.5 w-3.5" /> Ajouter</button>
        </div>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / référence facture (optionnel)" className="mt-2 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="budget-input-notes" />
      </form>

      {/* Expenses table */}
      <div className="overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-rose-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-5 py-3 text-left">Date</th>
              <th className="px-5 py-3 text-left">Libellé</th>
              <th className="px-5 py-3 text-left">Catégorie</th>
              <th className="px-5 py-3 text-right">Montant</th>
              <th className="px-5 py-3 text-left">Notes</th>
              <th className="px-5 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.expenses.map((e) => (
              <tr key={e.id} className="border-t border-neutral-100 hover:bg-rose-50/20" data-testid={`expense-row-${e.id}`}>
                <td className="px-5 py-3 text-xs text-neutral-500">{e.date || (e.created_at ? new Date(e.created_at).toLocaleDateString("fr") : "—")}</td>
                <td className="px-5 py-3 font-semibold">{e.label}</td>
                <td className="px-5 py-3"><span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-700">{e.category || "—"}</span></td>
                <td className="px-5 py-3 text-right font-bold text-rose-700">{(e.amount || 0).toLocaleString("fr")} €</td>
                <td className="px-5 py-3 text-xs text-neutral-500">{e.notes || ""}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setEditingExp({ ...e })} className="grid h-7 w-7 place-items-center rounded-full text-navy hover:bg-navy/10" title="Modifier" data-testid={`expense-edit-${e.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => deleteExpense(e)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`expense-del-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {data.expenses.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-neutral-500">Aucune dépense enregistrée. Ajoutez-en via le formulaire ci-dessus.</td></tr>}
          </tbody>
          {data.expenses.length > 0 && (
            <tfoot className="bg-rose-50/40">
              <tr>
                <td colSpan={3} className="px-5 py-3 text-right text-xs font-bold uppercase tracking-wider text-rose-700">Total dépenses</td>
                <td className="px-5 py-3 text-right font-bold text-rose-700">{data.total_expenses.toLocaleString("fr")} €</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Edit expense modal */}
      {editingExp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="expense-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setEditingExp(null)} aria-label="Fermer" />
          <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-600" /> Modifier la dépense</h2>
            <div className="mt-3 space-y-2">
              <input value={editingExp.label} onChange={(e) => setEditingExp({ ...editingExp, label: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder="Libellé" />
              <input type="number" step="0.01" value={editingExp.amount} onChange={(e) => setEditingExp({ ...editingExp, amount: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder="Montant (€)" />
              <select value={editingExp.category || ""} onChange={(e) => setEditingExp({ ...editingExp, category: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="date" value={editingExp.date || ""} onChange={(e) => setEditingExp({ ...editingExp, date: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <textarea value={editingExp.notes || ""} onChange={(e) => setEditingExp({ ...editingExp, notes: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" rows={2} placeholder="Notes" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditingExp(null)} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveEditExpense} className="rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="expense-save"><Save className="inline h-4 w-4" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 92ak — Orphan bulk-email modal removed: it was mis-placed inside
          BudgetPanel but referenced AdminEventDetail state (emailOpen,
          selectedAttendees, …), causing lint "no-undef" errors. The feature
          is still triggerable through the Participants tab actions. */}

    </div>
  );
}
